/**
 * Theme web components — register once, use in any section via custom element tags.
 */
(function () {
  function bindMiniCartQuantityActions(miniCart, refreshMiniCart, signal) {
    if (!miniCart || !window.routes?.cartChangeUrl || window.theme?.cartType === 'page') {
      return;
    }

    const updateQuantity = async (event, target) => {
      event.preventDefault();

      const line = target.getAttribute('data-line');
      if (!line) return;

      let parsedQuantity = 1;
      if (target.tagName === 'INPUT') {
        parsedQuantity = parseInt(target.value, 10);
      } else {
        parsedQuantity = parseInt(target.getAttribute('data-quantity') || '1', 10);
      }

      if (Number.isNaN(parsedQuantity)) return;

      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      try {
        const response = await fetch(`${window.routes.cartChangeUrl}.js`, {
          body: JSON.stringify({
            line,
            quantity: parsedQuantity,
          }),
          credentials: 'same-origin',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (response.ok) {
          await refreshMiniCart();
        }
      } finally {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      }
    };

    miniCart.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest(
          '[data-action="decrease-quantity"], [data-action="increase-quantity"]'
        );
        if (!target || !miniCart.contains(target)) return;
        updateQuantity(event, target);
      },
      { signal }
    );

    miniCart.addEventListener(
      'change',
      (event) => {
        const target = event.target.closest('.quantity-selector__value');
        if (!target || !miniCart.contains(target)) return;
        updateQuantity(event, target);
      },
      { signal }
    );

    miniCart.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter') return;
        if (!event.target.closest('.quantity-selector__value')) return;
        event.preventDefault();
      },
      { signal }
    );
  }

  function serializeProductForm(form) {
    const payload = {};

    new FormData(form).forEach((value, key) => {
      payload[key] = value;
    });

    return payload;
  }

  function getHeaderSectionEl() {
    return (
      document.getElementById('shopify-section-header') ||
      document.querySelector('.shopify-section__header') ||
      document.querySelector('[data-section-type="header"]')?.closest('[id^="shopify-section"]') ||
      null
    );
  }

  function getStickyHeaderScrollOffset(extra = 24) {
    const rootStyles = getComputedStyle(document.documentElement);
    const stickyFlag = parseInt(rootStyles.getPropertyValue('--header-is-sticky'), 10) || 0;
    const headerSection = getHeaderSectionEl();
    let offset = 0;

    if (headerSection) {
      const headerStyle = getComputedStyle(headerSection);
      const isSticky =
        stickyFlag === 1 || headerStyle.position === 'sticky' || headerStyle.position === 'fixed';
      if (isSticky) {
        offset = headerSection.getBoundingClientRect().height;
      }
    }

    if (!offset) {
      const headerHeight = parseInt(rootStyles.getPropertyValue('--header-height'), 10) || 0;
      offset = stickyFlag * headerHeight;
    }

    // Include any other sticky/fixed shopify sections currently stacked under the header
    // (e.g. sales bars).
    document.querySelectorAll('[id^="shopify-section"]').forEach((section) => {
      if (section === headerSection) return;
      const style = getComputedStyle(section);
      if (style.position !== 'sticky' && style.position !== 'fixed') return;
      if (style.display === 'none' || style.visibility === 'hidden') return;
      const rect = section.getBoundingClientRect();
      if (rect.height < 1) return;
      if (rect.top <= offset + 2 && rect.bottom > offset) {
        offset = rect.bottom;
      }
    });

    return Math.ceil(offset) + extra;
  }

  function scrollToElWithHeaderOffset(el, { behavior = 'smooth', extra = 24 } = {}) {
    if (!el) return;
    const offset = getStickyHeaderScrollOffset(extra);
    el.style.scrollMarginTop = `${offset}px`;
    const top = window.scrollY + el.getBoundingClientRect().top - offset;
    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  function initMiniCartDrawer({ cartRoot, signal, onOpenChange, openOnProductAdded = false }) {
    const noopDrawer = {
      setOpen() {},
      refresh() {
        return Promise.resolve();
      },
      get isOpen() {
        return false;
      },
    };

    if (!cartRoot || !window.routes?.cartUrl || window.theme?.cartType === 'page') {
      return noopDrawer;
    }

    const miniCart = cartRoot.querySelector('.mini-cart');
    const toggles = [...cartRoot.querySelectorAll('[data-action="toggle-mini-cart"]')];
    const shell = miniCart?.closest('[data-mini-cart-shell]') || null;
    const backdrop = shell?.querySelector('[data-mini-cart-backdrop]') || null;

    if (!miniCart || !toggles.length) {
      return noopDrawer;
    }

    const originalParent = shell?.parentElement || null;
    const originalNext = shell?.nextSibling || null;

    if (shell && shell.parentElement !== document.body) {
      document.body.appendChild(shell);
    }

    let isOpen = false;
    let lastToggle = toggles[0];

    const getCountEls = () => [
      ...cartRoot.querySelectorAll('.cko-header__cart-count, .header__cart-count'),
    ];

    const updateCounts = (itemCount) => {
      const count = parseInt(itemCount, 10);
      const safeCount = Number.isNaN(count) ? 0 : count;

      getCountEls().forEach((el) => {
        el.textContent = String(safeCount);
        el.removeAttribute('hidden');
      });

      miniCart.setAttribute('data-item-count', String(safeCount));
      if (window.theme) window.theme.cartCount = safeCount;
    };

    const setOpen = (open) => {
      const wasOpen = isOpen;
      isOpen = Boolean(open);

      toggles.forEach((toggle) => {
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
      miniCart.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      miniCart.classList.toggle('is-open', isOpen);
      shell?.classList.toggle('is-open', isOpen);

      if (backdrop) {
        backdrop.classList.toggle('is-open', isOpen);
      }

      document.body.classList.toggle('no-mobile-scroll', isOpen);
      document.documentElement.classList.toggle('cko-mini-cart-open', isOpen);

      if (isOpen && !wasOpen) {
        miniCart.querySelector('[data-action="close-mini-cart"]')?.focus?.();
      } else if (!isOpen && wasOpen) {
        lastToggle?.focus?.();
      }

      onOpenChange?.(isOpen, { toggle: lastToggle, miniCart, toggles });
    };

    const refreshMiniCart = async () => {
      const url = `${window.routes.cartUrl}?view=mini-cart&timestamp=${Date.now()}`;
      const response = await fetch(url, { credentials: 'same-origin' });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshMiniCart = doc.querySelector('.mini-cart');

      if (!freshMiniCart) return;

      const scrollEl = miniCart.querySelector('.mini-cart__line-item-list');
      const scrollTop = scrollEl?.scrollTop ?? 0;

      miniCart.innerHTML = freshMiniCart.innerHTML;

      const newScrollEl = miniCart.querySelector('.mini-cart__line-item-list');
      if (newScrollEl) newScrollEl.scrollTop = scrollTop;

      const itemCount = freshMiniCart.getAttribute('data-item-count');
      if (itemCount != null) updateCounts(itemCount);
    };

    const clearCart = async (event) => {
      event.preventDefault();
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      try {
        const response = await fetch(`${window.routes.cartUrl}/clear.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (response.ok) await refreshMiniCart();
      } finally {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      }
    };

    toggles.forEach((toggle) => {
      toggle.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          lastToggle = toggle;
          setOpen(!isOpen);
        },
        { signal }
      );
    });

    backdrop?.addEventListener('click', () => setOpen(false), { signal });

    miniCart.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-action="close-mini-cart"]')) {
          event.preventDefault();
          setOpen(false);
          return;
        }

        if (event.target.closest('[data-action="clear-mini-cart"]')) {
          clearCart(event);
        }
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!isOpen) return;
        if (miniCart.contains(event.target)) return;
        if (backdrop?.contains(event.target)) return;
        if (shell?.contains(event.target)) return;
        if (toggles.some((toggle) => toggle.contains(event.target))) return;
        if (event.target.closest('[data-action="add-to-cart"]')) return;
        setOpen(false);
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && isOpen) setOpen(false);
      },
      { signal }
    );

    document.addEventListener('cart:refresh', refreshMiniCart, { signal });
    document.addEventListener(
      'product:added',
      async () => {
        try {
          await refreshMiniCart();
          if (openOnProductAdded && window.theme?.cartType === 'drawer') {
            setOpen(true);
          }
        } finally {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
      },
      { signal }
    );

    /* AJAX add-to-cart for forms in #main (e.g. RTA Add Sample) so we open the drawer
       instead of a full-page cart redirect. Specialized handlers that stopPropagation
       (carousel, hero, etc.) still win. */
    document.addEventListener(
      'click',
      async (event) => {
        if (!window.routes?.cartAddUrl || window.theme?.cartType === 'page') return;

        const button = event.target.closest('#main [data-action="add-to-cart"]');
        if (!button) return;

        const form = button.closest('form[action*="/cart/add"]');
        if (!form) return;

        event.preventDefault();
        event.stopPropagation();

        if (button.hasAttribute('disabled')) return;

        button.setAttribute('disabled', 'disabled');
        document.dispatchEvent(new CustomEvent('theme:loading:start'));

        try {
          const response = await fetch(`${window.routes.cartAddUrl}.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(serializeProductForm(form)),
          });

          if (response.ok) {
            const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);

            document.dispatchEvent(
              new CustomEvent('product:added', {
                bubbles: true,
                detail: {
                  button,
                  variant: null,
                  quantity: Number.isNaN(quantity) ? 1 : quantity,
                },
              })
            );
          } else {
            document.dispatchEvent(new CustomEvent('theme:loading:end'));
          }
        } catch {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        } finally {
          button.removeAttribute('disabled');
        }
      },
      { signal }
    );

    bindMiniCartQuantityActions(miniCart, refreshMiniCart, signal);

    signal.addEventListener('abort', () => {
      setOpen(false);
      if (!shell) return;
      if (originalParent?.isConnected) {
        if (originalNext && originalNext.parentElement === originalParent) {
          originalParent.insertBefore(shell, originalNext);
        } else {
          originalParent.appendChild(shell);
        }
      } else if (shell.parentElement === document.body) {
        shell.remove();
      }
    });

    return {
      setOpen,
      refresh: refreshMiniCart,
      get isOpen() {
        return isOpen;
      },
    };
  }

  function extractSectionHtml(data, sectionId) {
    if (!data || typeof data !== 'object') return null;

    if (data.product && !data[sectionId] && !data.sections?.[sectionId]) {
      return null;
    }

    if (data.sections && typeof data.sections === 'object') {
      return data.sections[sectionId] || null;
    }

    return data[sectionId] || null;
  }

  async function fetchShopifySections(pathname, sectionIds) {
    const sectionsParam = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds;
    const response = await fetch(`${pathname}?sections=${sectionsParam}`, {
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error('Section fetch failed');

    return response.json();
  }

  function replaceSectionRoot(oldRoot, html, Controller) {
    const sectionId = oldRoot.getAttribute('data-section-id');
    const sectionType = oldRoot.getAttribute('data-section-type');

    Controller.destroyAll(oldRoot);
    oldRoot.outerHTML = html;

    if (!sectionId || !sectionType) return null;

    const newRoot = document.querySelector(
      `[data-section-type="${sectionType}"][data-section-id="${sectionId}"]`
    );

    if (newRoot) Controller.init(newRoot);

    return newRoot;
  }

  function initPagedCarousel({
    root,
    signal,
    cardsPerPage = 6,
    wrapSelector = '[data-pdp-hero-carousel-wrap]',
    carouselSelector = '[data-pdp-hero-style-carousel]',
    pageSelector = '[data-pdp-hero-carousel-page]',
    itemSelector = '[data-pdp-hero-style-item]',
    countSelector = '[data-pdp-hero-style-count]',
    prevSelector = '[data-pdp-hero-car-prev]',
    nextSelector = '[data-pdp-hero-car-next]',
    dotsSelector = '[data-pdp-hero-car-dots]',
    dotSelector = '[data-pdp-hero-car-dot]',
  }) {
    const carouselWrap = root.querySelector(wrapSelector);
    const carousel = root.querySelector(carouselSelector);
    const pages = carousel ? [...carousel.querySelectorAll(pageSelector)] : [];
    const items = carousel ? [...carousel.querySelectorAll(itemSelector)] : [];
    const countEl = root.querySelector(countSelector);
    const prevBtn = root.querySelector(prevSelector);
    const nextBtn = root.querySelector(nextSelector);
    const dotsEl = root.querySelector(dotsSelector);
    const perPage = parseInt(
      carouselWrap?.getAttribute('data-carousel-cards-per-page') || String(cardsPerPage),
      10
    );
    let carPage = 0;

    const selectedIndex = items.findIndex((item) => item.classList.contains('is-selected'));
    if (selectedIndex >= 0) {
      carPage = Math.floor(selectedIndex / perPage);
    }

    const totalPages = Math.max(1, pages.length || Math.ceil(items.length / perPage));

    const renderCarousel = () => {
      if (!items.length) return;

      if (carPage >= totalPages) carPage = 0;

      pages.forEach((page, index) => {
        page.classList.toggle('is-active', index === carPage);
      });

      const start = carPage * perPage + 1;
      const end = Math.min((carPage + 1) * perPage, items.length);

      if (countEl) {
        countEl.textContent =
          items.length === 1 ? '1 style' : `${items.length} styles, showing ${start}–${end}`;
      }

      const hasMultiplePages = totalPages > 1;
      carouselWrap?.classList.toggle('is-single-page', !hasMultiplePages);
      prevBtn?.toggleAttribute('disabled', !hasMultiplePages || carPage === 0);
      nextBtn?.toggleAttribute('disabled', !hasMultiplePages || carPage >= totalPages - 1);
      dotsEl?.classList.toggle('is-hidden', !hasMultiplePages);

      dotsEl?.querySelectorAll(dotSelector).forEach((dot, index) => {
        dot.classList.toggle('is-active', index === carPage);
      });
    };

    renderCarousel();

    prevBtn?.addEventListener(
      'click',
      () => {
        carPage = Math.max(0, carPage - 1);
        renderCarousel();
      },
      { signal }
    );

    nextBtn?.addEventListener(
      'click',
      () => {
        carPage = Math.min(totalPages - 1, carPage + 1);
        renderCarousel();
      },
      { signal }
    );

    dotsEl?.addEventListener(
      'click',
      (event) => {
        const dot = event.target.closest(dotSelector);
        if (!dot) return;
        carPage = parseInt(dot.getAttribute('data-pdp-hero-car-dot') || '0', 10);
        renderCarousel();
      },
      { signal }
    );
  }

  function createSectionController(Controller, rootSelector) {
    const instances = new WeakMap();

    Controller.init = function init(root) {
      if (!root || instances.has(root)) return;
      instances.set(root, new Controller(root));
    };

    Controller.initAll = function initAll(scope = document) {
      scope.querySelectorAll(rootSelector).forEach((root) => Controller.init(root));
    };

    Controller.destroyAll = function destroyAll(scope) {
      if (!scope) return;

      if (scope.matches?.(rootSelector)) {
        instances.get(scope)?.destroy();
        instances.delete(scope);
      }

      scope.querySelectorAll(rootSelector).forEach((root) => {
        instances.get(root)?.destroy();
        instances.delete(root);
      });
    };

    Controller.getInstance = function getInstance(root) {
      return instances.get(root);
    };

    return instances;
  }

  const sectionControllers = [];

  function registerSectionController(Controller, rootSelector, options = {}) {
    createSectionController(Controller, rootSelector);
    sectionControllers.push({ Controller, options });
    Controller.initAll();
  }

  function initSectionControllers(scope) {
    sectionControllers.forEach(({ Controller, options }) => {
      if (options.beforeInitAll) options.beforeInitAll();
      Controller.initAll(scope);
    });
  }

  function destroySectionControllers(scope) {
    sectionControllers.forEach(({ Controller }) => {
      Controller.destroyAll(scope);
    });
  }

  class AeoNavbar extends HTMLElement {
    #abortController = new AbortController();
    #miniCartDrawer = null;

    connectedCallback() {
      const signal = this.#abortController.signal;
      this.#initMobileNav(signal);
      this.#initScrollLinks(signal);
      this.#initCart(signal);
    }

    #initScrollLinks(signal) {
      const scrollLinks = this.querySelectorAll('[data-aeo-nav-scroll]');
      const mobileQuery = window.matchMedia('(max-width: 749px)');

      const closeMobileMenu = () => {
        if (mobileQuery.matches) {
          this.classList.remove('is-open');
          const toggle = this.querySelector('[data-aeo-nav-toggle]');
          toggle?.setAttribute('aria-expanded', 'false');
        }
      };

      scrollLinks.forEach((link) => {
        link.addEventListener(
          'click',
          (event) => {
            const targetSelector = link.getAttribute('href');
            if (!targetSelector?.startsWith('#')) return;

            const target = document.querySelector(targetSelector);
            if (!target) return;

            event.preventDefault();

            const navHeight =
              this.offsetHeight ||
              parseInt(
                getComputedStyle(this.closest('.shopify-section') || this)
                  .getPropertyValue('--aeo-nav-height'),
                10
              ) ||
              58;

            const top =
              target.getBoundingClientRect().top + window.scrollY - navHeight;

            window.scrollTo({ top, behavior: 'smooth' });
            closeMobileMenu();
          },
          { signal }
        );
      });
    }

    #initMobileNav(signal) {
      const toggle = this.querySelector('[data-aeo-nav-toggle]');
      const menu = this.querySelector('[data-aeo-nav-menu]');

      if (!toggle || !menu) return;

      const mobileQuery = window.matchMedia('(max-width: 749px)');

      const setOpen = (open) => {
        this.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      };

      const closeIfMobile = () => {
        if (mobileQuery.matches) setOpen(false);
      };

      toggle.addEventListener(
        'click',
        () => {
          if (!mobileQuery.matches) return;
          setOpen(!this.classList.contains('is-open'));
        },
        { signal }
      );

      menu.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', closeIfMobile, { signal });
      });

      mobileQuery.addEventListener(
        'change',
        () => {
          if (!mobileQuery.matches) setOpen(false);
        },
        { signal }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') closeIfMobile();
        },
        { signal }
      );
    }

    #initCart(signal) {
      const cartRoot = this.querySelector('.aeo-navbar__cart');

      if (!cartRoot || !window.routes?.cartAddUrl || window.theme?.cartType === 'page') {
        return;
      }

      this.#miniCartDrawer = initMiniCartDrawer({
        cartRoot,
        signal,
        onOpenChange: (open, { toggle }) => {
          toggle.querySelector('.header__cart-icon')?.setAttribute('aria-expanded', open ? 'true' : 'false');
        },
      });

      document.addEventListener(
        'click',
        async (event) => {
          const button = event.target.closest('#main [data-action="add-to-cart"]');

          if (!button || window.theme?.cartType === 'page') return;

          const form = button.closest('form[action*="/cart/add"]');
          if (!form) return;

          event.preventDefault();
          event.stopPropagation();
          button.setAttribute('disabled', 'disabled');
          document.dispatchEvent(new CustomEvent('theme:loading:start'));

          try {
            const response = await fetch(`${window.routes.cartAddUrl}.js`, {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              body: JSON.stringify(serializeProductForm(form)),
            });

            if (response.ok) {
              await this.#miniCartDrawer.refresh();

              if (window.theme?.cartType === 'drawer') {
                this.#miniCartDrawer.setOpen(true);
              }
            }
          } finally {
            button.removeAttribute('disabled');
            document.dispatchEvent(new CustomEvent('theme:loading:end'));
          }
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      this.classList.remove('is-open');
      this.#miniCartDrawer = null;
      document.body.classList.remove('no-mobile-scroll');
    }
  }

  class AeoContentHub extends HTMLElement {
    #abortController = new AbortController();

    connectedCallback() {
      const signal = this.#abortController.signal;
      this.#initFilters(signal);
      this.#initLoadMore(signal);
    }

    #getSectionRoot() {
      return (
        this.closest('.shopify-section') ??
        this.closest('[id^="shopify-section-"]') ??
        this
      );
    }

    #initFilters(signal) {
      const section = this.#getSectionRoot();
      const tabs = section.querySelectorAll('[data-aeo-filter-tab]');
      const filterCount = section.querySelector('[data-aeo-filter-count]');
      const loadMoreWrap = this.querySelector('[data-aeo-load-more-wrap]');
      const featured = this.querySelector('.aeo-content-hub__featured');
      const cards = this.querySelectorAll('.aeo-content-hub__card');

      if (!tabs.length) return;

      const FILTER_HIDDEN_CARD = 'aeo-content-hub__card--filter-hidden';
      const FILTER_HIDDEN_FEATURED = 'aeo-content-hub__featured--filter-hidden';
      const LOAD_MORE_HIDDEN = 'aeo-content-hub__card--load-more-hidden';
      const LOAD_MORE_WRAP_HIDDEN = 'aeo-content-hub__load-more--hidden';

      const isShown = (element) =>
        element &&
        !element.classList.contains(FILTER_HIDDEN_CARD) &&
        !element.classList.contains(FILTER_HIDDEN_FEATURED) &&
        !element.classList.contains(LOAD_MORE_HIDDEN);

      const applyFilter = (category) => {
        const showAll = category === 'All';
        let visibleCount = 0;

        const toggleItem = (element) => {
          if (!element) return;

          const itemCategory = element.getAttribute('data-category');
          const matchesFilter = showAll || itemCategory === category;
          const isFeatured = element.classList.contains('aeo-content-hub__featured');
          const filterHiddenClass = isFeatured
            ? FILTER_HIDDEN_FEATURED
            : FILTER_HIDDEN_CARD;

          element.classList.toggle(filterHiddenClass, !matchesFilter);

          if (matchesFilter && !isFeatured && !showAll) {
            element.classList.remove(LOAD_MORE_HIDDEN);
          }

          if (matchesFilter && isShown(element)) {
            visibleCount += 1;
          }
        };

        toggleItem(featured);
        cards.forEach(toggleItem);

        if (!showAll) {
          loadMoreWrap?.classList.add(LOAD_MORE_WRAP_HIDDEN);
        } else {
          const hasPaginatedCards = [...cards].some(
            (card) =>
              card.classList.contains(LOAD_MORE_HIDDEN) &&
              !card.classList.contains(FILTER_HIDDEN_CARD)
          );
          loadMoreWrap?.classList.toggle(LOAD_MORE_WRAP_HIDDEN, !hasPaginatedCards);
        }

        if (filterCount) {
          filterCount.textContent = `${visibleCount} article${visibleCount === 1 ? '' : 's'}`;
        }
      };

      tabs.forEach((tab) => {
        tab.addEventListener(
          'click',
          () => {
            tabs.forEach((t) =>
              t.classList.toggle('aeo-content-hub__filter-tab--active', t === tab)
            );
            applyFilter(tab.getAttribute('data-cat'));
          },
          { signal }
        );
      });
    }

    #initLoadMore(signal) {
      const loadMoreBtn = this.querySelector('[data-aeo-load-more]');
      const loadMoreWrap = this.querySelector('[data-aeo-load-more-wrap]');
      const grid = this.querySelector('[data-aeo-article-grid]');

      if (!loadMoreBtn || !grid) return;

      const hiddenCards = () =>
        grid.querySelectorAll('.aeo-content-hub__card--load-more-hidden');

      if (hiddenCards().length === 0) {
        loadMoreWrap?.classList.add('aeo-content-hub__load-more--hidden');
        return;
      }

      loadMoreBtn.addEventListener(
        'click',
        () => {
          hiddenCards().forEach((card) => {
            card.classList.remove('aeo-content-hub__card--load-more-hidden');
          });
          loadMoreWrap?.classList.add('aeo-content-hub__load-more--hidden');
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }
  }

  class AeoFreeSamples extends HTMLElement {
    #abortController = new AbortController();
    #index = 0;

    connectedCallback() {
      const signal = this.#abortController.signal;
      const viewport = this.querySelector('[data-aeo-samples-viewport]');
      const track = this.querySelector('[data-aeo-samples-track]');
      const cards = this.querySelectorAll('[data-aeo-sample]');
      const prevBtn = this.querySelector('[data-aeo-samples-prev]');
      const nextBtn = this.querySelector('[data-aeo-samples-next]');
      const counter = this.querySelector('[data-aeo-samples-counter]');
      const dotsContainer = this.querySelector('[data-aeo-samples-dots]');

      if (!viewport || !track || !cards.length) return;

      const gap = 20;
      const desktopQuery = window.matchMedia('(min-width: 990px)');
      const tabletQuery = window.matchMedia('(min-width: 750px)');

      const getPerPage = () => {
        if (desktopQuery.matches) return 4;
        if (tabletQuery.matches) return 2;
        return 1;
      };

      const getMaxIndex = () => Math.max(0, cards.length - getPerPage());

      const setDimensions = () => {
        const perPage = getPerPage();
        const viewportWidth = viewport.clientWidth;
        const slideWidth = (viewportWidth - gap * (perPage - 1)) / perPage;

        cards.forEach((card) => {
          card.style.flexBasis = `${slideWidth}px`;
          card.style.width = `${slideWidth}px`;
        });

        return { perPage, slideWidth };
      };

      const renderDots = (dotCount) => {
        if (!dotsContainer) return;

        dotsContainer.replaceChildren();

        for (let dotIndex = 0; dotIndex < dotCount; dotIndex += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'aeo-free-samples__dot';
          dot.setAttribute('aria-label', `Go to sample ${dotIndex + 1}`);
          dot.addEventListener(
            'click',
            () => {
              this.#index = dotIndex;
              update();
            },
            { signal }
          );
          dotsContainer.appendChild(dot);
        }
      };

      const update = () => {
        const { perPage, slideWidth } = setDimensions();
        const maxIndex = getMaxIndex();
        const step = slideWidth + gap;

        this.#index = Math.min(this.#index, maxIndex);

        track.style.transform = `translateX(-${this.#index * step}px)`;

        const start = this.#index + 1;
        const end = Math.min(this.#index + perPage, cards.length);

        if (counter) {
          counter.textContent = `${start}–${end} of ${cards.length}`;
        }

        if (prevBtn) prevBtn.disabled = this.#index === 0;
        if (nextBtn) nextBtn.disabled = this.#index >= maxIndex;

        const dots = dotsContainer?.querySelectorAll('.aeo-free-samples__dot');
        dots?.forEach((dot, dotIndex) => {
          dot.classList.toggle('aeo-free-samples__dot--active', dotIndex === this.#index);
          dot.setAttribute('aria-current', dotIndex === this.#index ? 'true' : 'false');
        });
      };

      renderDots(getMaxIndex() + 1);
      update();

      prevBtn?.addEventListener(
        'click',
        () => {
          this.#index -= 1;
          update();
        },
        { signal }
      );

      nextBtn?.addEventListener(
        'click',
        () => {
          this.#index += 1;
          update();
        },
        { signal }
      );

      const onResize = () => {
        this.#index = Math.min(this.#index, getMaxIndex());
        renderDots(getMaxIndex() + 1);
        update();
      };

      window.addEventListener('resize', onResize, { signal });

      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(onResize);
        observer.observe(viewport);
        signal.addEventListener('abort', () => observer.disconnect());
      }

      desktopQuery.addEventListener('change', onResize, { signal });
      tabletQuery.addEventListener('change', onResize, { signal });
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      this.#index = 0;
    }
  }

  class ListAccordion extends HTMLElement {
    #abortController = new AbortController();

    static ITEM_SELECTOR = '.list-accordion__item';
    static TRIGGER_SELECTOR = '.list-accordion__trigger';
    static OPEN_CLASS = 'list-accordion__item--open';

    connectedCallback() {
      this.addEventListener('click', this.#onClick, {
        signal: this.#abortController.signal,
      });
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    #onClick = (event) => {
      const trigger = event.target.closest(ListAccordion.TRIGGER_SELECTOR);
      if (!trigger || !this.contains(trigger)) return;

      const item = trigger.closest(ListAccordion.ITEM_SELECTOR);
      if (!item) return;

      const isOpen = item.classList.contains(ListAccordion.OPEN_CLASS);

      this.querySelectorAll(`${ListAccordion.ITEM_SELECTOR}.${ListAccordion.OPEN_CLASS}`).forEach((openItem) => {
        if (openItem !== item) this.#setOpen(openItem, false);
      });

      this.#setOpen(item, !isOpen);
    };

    #setOpen(item, open) {
      const trigger = item.querySelector(ListAccordion.TRIGGER_SELECTOR);
      item.classList.toggle(ListAccordion.OPEN_CLASS, open);
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  class CollectionHero extends HTMLElement {
    #abortController = new AbortController();

    static ITEM_SELECTOR = '.list-accordion__item';
    static TRIGGER_SELECTOR = '.list-accordion__trigger';
    static OPEN_CLASS = 'list-accordion__item--open';

    connectedCallback() {
      this.addEventListener('click', this.#onClick, {
        signal: this.#abortController.signal,
      });
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    #onClick = async (event) => {
      const scrollLink = event.target.closest('[data-scroll-to]');
      if (scrollLink && this.contains(scrollLink)) {
        const targetId = scrollLink.getAttribute('data-scroll-to');
        const target = targetId ? document.getElementById(targetId) : null;
        if (target) {
          event.preventDefault();
          const scrollTarget = target.querySelector('.collection-products__tabs') || target;
          scrollToElWithHeaderOffset(scrollTarget);
          if (window.history?.replaceState) {
            window.history.replaceState(null, '', `#${targetId}`);
          }
          return;
        }
      }

      const accordionTrigger = event.target.closest(CollectionHero.TRIGGER_SELECTOR);
      if (accordionTrigger && this.contains(accordionTrigger)) {
        event.preventDefault();
        this.#toggleAccordion(accordionTrigger);
        return;
      }

      const button = event.target.closest('[data-action="add-to-cart"]');
      if (!button || !this.contains(button)) return;
      if (!window.routes?.cartAddUrl || window.theme?.cartType === 'page') return;

      const form = button.closest('form[action*="/cart/add"]');
      if (!form || !this.contains(form)) return;

      event.preventDefault();
      event.stopPropagation();

      button.setAttribute('disabled', 'disabled');
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      try {
        const response = await fetch(`${window.routes.cartAddUrl}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(serializeProductForm(form)),
        });

        if (response.ok) {
          const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);

          this.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                button,
                variant: null,
                quantity: Number.isNaN(quantity) ? 1 : quantity,
              },
            })
          );
        } else {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
      } catch {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      } finally {
        button.removeAttribute('disabled');
      }
    };

    #toggleAccordion(trigger) {
      const item = trigger.closest(CollectionHero.ITEM_SELECTOR);
      if (!item) return;

      const isOpen = item.classList.contains(CollectionHero.OPEN_CLASS);
      const accordion = item.closest('[data-collection-hero-accordion]') || this;

      accordion.querySelectorAll(`${CollectionHero.ITEM_SELECTOR}.${CollectionHero.OPEN_CLASS}`).forEach((openItem) => {
        if (openItem !== item) this.#setAccordionOpen(openItem, false);
      });

      this.#setAccordionOpen(item, !isOpen);
    }

    #setAccordionOpen(item, open) {
      const trigger = item.querySelector(CollectionHero.TRIGGER_SELECTOR);
      item.classList.toggle(CollectionHero.OPEN_CLASS, open);
      if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  }

  class CollectionProducts extends HTMLElement {
    #abortController = new AbortController();

    static VALID_CATEGORIES = {
      'base-cabinets': true,
      'wall-cabinets': true,
      'oven-pantry-cabinets': true,
      accessories: true,
    };

    static TAB_ACTIVE = 'collection-products__tab--active';
    static PANEL_ACTIVE = 'collection-products__category-panel--active';
    static QUICK_ACTIVE = 'collection-products__quick-link--active';

    connectedCallback() {
      this.addEventListener('click', this.#onClick, {
        signal: this.#abortController.signal,
      });

      this.#syncScrollMargin();
      window.addEventListener('resize', this.#syncScrollMargin, {
        signal: this.#abortController.signal,
      });

      const initialCategory = this.#normalizeCategory(
        new URLSearchParams(window.location.search).get('category')
      );
      this.#activateCategory(initialCategory, { updateUrl: false });
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    #syncScrollMargin = () => {
      const offset = `${getStickyHeaderScrollOffset(16)}px`;
      this.style.scrollMarginTop = offset;
      const tabs = this.querySelector('.collection-products__tabs');
      if (tabs) tabs.style.scrollMarginTop = offset;
    };

    #onClick = async (event) => {
      const tab = event.target.closest('.collection-products__tab[data-cabinet-category]');
      if (tab && this.contains(tab)) {
        const category = tab.getAttribute('data-cabinet-category');
        if (!category) return;
        this.#activateCategory(category, { scrollToTop: true });
        return;
      }

      const quickLink = event.target.closest('.collection-products__quick-link[data-scroll-target]');
      if (quickLink && this.contains(quickLink)) {
        this.#onQuickLink(event, quickLink);
        return;
      }

      const stepBtn = event.target.closest('[data-stepper] [data-step]');
      if (stepBtn && this.contains(stepBtn)) {
        this.#onStepper(stepBtn);
        return;
      }

      const button = event.target.closest('[data-action="add-to-cart"]');
      if (!button || !this.contains(button)) return;
      if (!window.routes?.cartAddUrl || window.theme?.cartType === 'page') return;

      const form = button.closest('form[action*="/cart/add"]');
      if (!form || !this.contains(form)) return;

      event.preventDefault();
      event.stopPropagation();

      button.setAttribute('disabled', 'disabled');
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      try {
        const response = await fetch(`${window.routes.cartAddUrl}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(serializeProductForm(form)),
        });

        if (response.ok) {
          const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);

          this.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                button,
                variant: null,
                quantity: Number.isNaN(quantity) ? 1 : quantity,
              },
            })
          );
        } else {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        }
      } catch {
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      } finally {
        button.removeAttribute('disabled');
      }
    };

    #normalizeCategory(category) {
      if (!category) return 'base-cabinets';
      category = category.toLowerCase().replace(/_/g, '-');
      if (category === 'base-cabinet') return 'base-cabinets';
      if (category === 'wall-cabinet') return 'wall-cabinets';
      if (category === 'oven-pantry-cabinet') return 'oven-pantry-cabinets';
      if (category === 'accessory') return 'accessories';
      return CollectionProducts.VALID_CATEGORIES[category] ? category : 'base-cabinets';
    }

    #activateCategory(category, options = {}) {
      category = this.#normalizeCategory(category);
      this.setAttribute('data-active-category', category);

      this.querySelectorAll('.collection-products__category-panel').forEach((panel) => {
        const isActive = panel.getAttribute('data-category-panel') === category;
        panel.classList.toggle(CollectionProducts.PANEL_ACTIVE, isActive);
        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', 'hidden');
        }
      });

      this.querySelectorAll('.collection-products__tab').forEach((tab) => {
        const isActive = tab.getAttribute('data-cabinet-category') === category;
        tab.classList.toggle(CollectionProducts.TAB_ACTIVE, isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      if (options.updateUrl !== false && window.history?.replaceState) {
        const url = new URL(window.location.href);
        if (category === 'base-cabinets') {
          url.searchParams.delete('category');
        } else {
          url.searchParams.set('category', category);
        }
        window.history.replaceState(null, '', url.toString());
      }

      if (options.scrollToTop) {
        const panelTop = this.querySelector('.collection-products__tabs') || this.querySelector('[data-collection-products-panel]');
        if (panelTop) {
          scrollToElWithHeaderOffset(panelTop);
        }
      }

      this.#initQuickLinkRows();
    }

    #onQuickLink(event, link) {
      const panel = link.closest('.collection-products__category-panel');
      if (!panel || !panel.classList.contains(CollectionProducts.PANEL_ACTIVE)) return;

      event.preventDefault();
      event.stopPropagation();

      const targetId = link.getAttribute('data-scroll-target');
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target) return;

      scrollToElWithHeaderOffset(target);
    }

    #initQuickLinkRows() {
      this.querySelectorAll('.collection-products__quick[data-current-group]').forEach((row) => {
        const current = row.getAttribute('data-current-group');
        if (!current) return;
        row.querySelectorAll('.collection-products__quick-link').forEach((item) => {
          item.classList.toggle(
            CollectionProducts.QUICK_ACTIVE,
            item.getAttribute('data-scroll-target') === current
          );
        });
      });
    }

    #onStepper(stepBtn) {
      const stepper = stepBtn.closest('[data-stepper]');
      const input = stepper?.querySelector('input[type="number"]');
      if (!input) return;

      const value = parseInt(input.value, 10) || 1;
      const step = stepBtn.getAttribute('data-step');
      if (step === 'down' && value > 1) {
        input.value = value - 1;
      } else if (step === 'up') {
        input.value = value + 1;
      }
    }
  }

  class LpHeroCarousel extends HTMLElement {
    #abortController = new AbortController();
    #intervalId = null;
    #current = 0;

    static ACTIVE_CLASS = 'lp-hero__slide--active';

    connectedCallback() {
      const signal = this.#abortController.signal;
      const slides = this.querySelectorAll('[data-lp-hero-slide]');

      if (slides.length < 2) return;

      const mode = this.getAttribute('data-carousel-mode') || 'auto';
      const intervalSeconds = parseInt(this.getAttribute('data-carousel-interval'), 10) || 5;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const goTo = (index) => {
        this.#current = ((index % slides.length) + slides.length) % slides.length;

        slides.forEach((slide, slideIndex) => {
          const isActive = slideIndex === this.#current;
          slide.classList.toggle(LpHeroCarousel.ACTIVE_CLASS, isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
      };

      const next = () => goTo(this.#current + 1);
      const prev = () => goTo(this.#current - 1);

      const stopAutoplay = () => {
        if (this.#intervalId) {
          window.clearInterval(this.#intervalId);
          this.#intervalId = null;
        }
      };

      const startAutoplay = () => {
        if (mode !== 'auto' || reducedMotion.matches) return;
        stopAutoplay();
        this.#intervalId = window.setInterval(next, intervalSeconds * 1000);
      };

      goTo(0);
      startAutoplay();

      if (mode === 'manual') {
        const prevBtn = this.querySelector('[data-lp-hero-prev]');
        const nextBtn = this.querySelector('[data-lp-hero-next]');

        prevBtn?.addEventListener('click', () => prev(), { signal });
        nextBtn?.addEventListener('click', () => next(), { signal });
      }

      if (mode === 'auto') {
        reducedMotion.addEventListener(
          'change',
          () => {
            if (reducedMotion.matches) {
              stopAutoplay();
            } else {
              startAutoplay();
            }
          },
          { signal }
        );

        this.addEventListener('mouseenter', stopAutoplay, { signal });
        this.addEventListener('mouseleave', startAutoplay, { signal });

        document.addEventListener(
          'visibilitychange',
          () => {
            if (document.hidden) {
              stopAutoplay();
            } else {
              startAutoplay();
            }
          },
          { signal }
        );
      }
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      if (this.#intervalId) {
        window.clearInterval(this.#intervalId);
        this.#intervalId = null;
      }
      this.#current = 0;
    }
  }

  class HeaderNavbarCta {
    #abortController = new AbortController();
    #root;
    #resizeObserver = null;

    static SCROLL_THRESHOLD = 8;
    static ROOT_SELECTOR = 'section.header-navbar-cta';
    static SCROLLED_CLASS = 'header-navbar-cta--scrolled';
    static BAR_SELECTOR = '.header-navbar-cta__bar';
    static ANNOUNCEMENT_SELECTOR = '[data-header-navbar-cta-announcement]';
    static SPACER_SELECTOR = '.header-navbar-cta__spacer';
    static SECTION_VAR = '--header-navbar-cta-offset';

    constructor(root) {
      this.#root = root;
      this.#connect();
    }

    #connect() {
      const signal = this.#abortController.signal;
      const bar = this.#root.querySelector(HeaderNavbarCta.BAR_SELECTOR);
      const announcement = this.#root.querySelector(HeaderNavbarCta.ANNOUNCEMENT_SELECTOR);
      const sectionEl = this.#root.closest('.shopify-section');
      const spacer = sectionEl?.querySelector(HeaderNavbarCta.SPACER_SELECTOR) ?? null;

      if (!bar) return;

      const syncLayout = () => {
        const scrolled = window.scrollY > HeaderNavbarCta.SCROLL_THRESHOLD;
        const barHeight = bar.offsetHeight;
        const announcementHeight =
          announcement && !scrolled ? announcement.scrollHeight : 0;
        const offset = barHeight + announcementHeight;

        this.#root.classList.toggle(HeaderNavbarCta.SCROLLED_CLASS, scrolled);

        if (announcement) {
          announcement.setAttribute('aria-hidden', scrolled ? 'true' : 'false');
        }

        if (sectionEl) {
          sectionEl.style.setProperty(HeaderNavbarCta.SECTION_VAR, `${offset}px`);
        }

        if (spacer) {
          spacer.style.height = `${offset}px`;
        }
      };

      const onScroll = () => {
        syncLayout();
      };

      syncLayout();
      window.addEventListener('scroll', onScroll, { passive: true, signal });
      window.addEventListener('resize', syncLayout, { signal });

      if (typeof ResizeObserver !== 'undefined' && announcement) {
        this.#resizeObserver = new ResizeObserver(syncLayout);
        this.#resizeObserver.observe(announcement);
        this.#resizeObserver.observe(bar);
        signal.addEventListener('abort', () => {
          this.#resizeObserver?.disconnect();
          this.#resizeObserver = null;
        });
      }
    }

    destroy() {
      this.#resizeObserver?.disconnect();
      this.#resizeObserver = null;
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    static #instances = new WeakMap();

    static init(root) {
      if (!root || HeaderNavbarCta.#instances.has(root)) return;
      HeaderNavbarCta.#instances.set(root, new HeaderNavbarCta(root));
    }

    static initAll(scope = document) {
      scope.querySelectorAll(HeaderNavbarCta.ROOT_SELECTOR).forEach((root) => {
        HeaderNavbarCta.init(root);
      });
    }

    static destroyAll(scope) {
      scope.querySelectorAll(HeaderNavbarCta.ROOT_SELECTOR).forEach((root) => {
        HeaderNavbarCta.#instances.get(root)?.destroy();
        HeaderNavbarCta.#instances.delete(root);
      });
    }
  }

  HeaderNavbarCta.initAll();

  document.addEventListener('shopify:section:load', (event) => {
    HeaderNavbarCta.initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    HeaderNavbarCta.destroyAll(event.target);
  });

  class PdpHero {
    #abortController = new AbortController();
    #root;

    static ROOT_SELECTOR = '[data-section-type="pdp-hero"]';
    static CARDS_PER_PAGE = 3;
    static SAMPLE_SELECTOR_VIEW_STORAGE_KEY = 'pdp-hero-sample-selector-view';

    static getSampleSelectorView() {
      try {
        const stored = sessionStorage.getItem(PdpHero.SAMPLE_SELECTOR_VIEW_STORAGE_KEY);
        return stored === 'list' ? 'list' : 'carousel';
      } catch {
        return 'carousel';
      }
    }

    static persistSampleSelectorView(mode) {
      const viewMode = mode === 'list' ? 'list' : 'carousel';

      try {
        sessionStorage.setItem(PdpHero.SAMPLE_SELECTOR_VIEW_STORAGE_KEY, viewMode);
      } catch {
        // sessionStorage unavailable (e.g. private browsing)
      }

      return viewMode;
    }

    constructor(root) {
      this.#root = root;
      this.#connect();
    }

    static bindPopstate() {
      if (PdpHero._popstateBound) return;
      PdpHero._popstateBound = true;

      window.addEventListener('popstate', () => {
        const root = document.querySelector(PdpHero.ROOT_SELECTOR);
        if (!root) return;

        let instance = PdpHero.getInstance(root);
        if (!instance) {
          PdpHero.init(root);
          instance = PdpHero.getInstance(root);
        }
        if (!instance) return;

        instance.#navigateToProduct(window.location.pathname, {
          pushState: false,
          force: true,
        });
      });
    }

    #showStatus(message, type = 'success') {
      const statusEl = this.#root.querySelector('[data-pdp-hero-form-status]');
      if (!statusEl) return;

      statusEl.hidden = false;
      statusEl.innerHTML = `<p class="alert alert--${type === 'success' ? 'success' : 'error'}">${message}</p>`;

      window.setTimeout(() => {
        statusEl.hidden = true;
        statusEl.innerHTML = '';
      }, 5500);
    }

    async #addToCart(form, button) {
      if (!window.routes?.cartAddUrl) {
        form.submit();
        return;
      }

      if (window.theme?.cartType === 'page') {
        form.submit();
        return;
      }

      button?.setAttribute('disabled', 'disabled');
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      try {
        const response = await fetch(`${window.routes.cartAddUrl}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(serializeProductForm(form)),
        });

        if (response.ok) {
          document.dispatchEvent(
            new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                quantity: parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10),
              },
            })
          );

          if (window.theme?.cartType === 'drawer') {
            return true;
          }

          return true;
        }

        const content = await response.json();
        this.#showStatus(content.description || 'Unable to add to cart.', 'error');
        return false;
      } catch {
        this.#showStatus('Unable to add to cart.', 'error');
        return false;
      } finally {
        button?.removeAttribute('disabled');
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      }
    }

    static #extractSectionHtml(data, sectionId) {
      return extractSectionHtml(data, sectionId);
    }

    #updatePageMeta(newRoot) {
      const productTitle = newRoot.querySelector('[data-pdp-hero-sel-name]')?.textContent?.trim();

      if (productTitle) {
        const shopSuffix = document.title.includes('|')
          ? document.title.split('|').slice(1).join('|').trim()
          : '';
        document.title = shopSuffix ? `${productTitle} | ${shopSuffix}` : productTitle;
      }
    }

    static #productHandleFromPath(pathname) {
      return pathname.match(/\/products\/([^/?#]+)/)?.[1]?.replace(/\/sample-door$/, '') || '';
    }

    static selectProduct(url, { pushState = true, force = false, scrollToTop = false } = {}) {
      const target = new URL(url, window.location.origin);
      if (!target.pathname.endsWith('/sample-door')) {
        target.pathname = `${target.pathname.replace(/\/$/, '')}/sample-door`;
      }

      if (scrollToTop) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }

      const root = document.querySelector(PdpHero.ROOT_SELECTOR);
      if (!root) {
        window.location.href = target.pathname;
        return;
      }

      let instance = PdpHero.getInstance(root);
      if (!instance) {
        PdpHero.init(root);
        instance = PdpHero.getInstance(root);
      }

      if (!instance) return;

      if (!force && target.pathname === window.location.pathname) {
        PdpHero.#dispatchProductChanged(target.pathname);
        return;
      }

      instance.#navigateToProduct(target.pathname, { pushState, force, scrollToHero: false });
    }

    static #dispatchProductChanged(pathname) {
      document.dispatchEvent(
        new CustomEvent('pdp-hero:product-changed', {
          detail: {
            pathname,
            handle: PdpHero.#productHandleFromPath(pathname),
          },
        })
      );
    }

    static #preserveScrollPosition(callback) {
      const scrollY = window.scrollY;
      callback();
      const restore = () => window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
      restore();
      requestAnimationFrame(restore);
    }

    async #navigateToProduct(url, { pushState = true, force = false, scrollToHero = false } = {}) {
      const sectionId = this.#root.getAttribute('data-section-id');
      if (!sectionId) return;

      const target = new URL(url, window.location.origin);
      if (!target.pathname.endsWith('/sample-door')) {
        target.pathname = `${target.pathname.replace(/\/$/, '')}/sample-door`;
      }

      if (!force && target.pathname === window.location.pathname) return;

      this.#root.classList.add('is-loading');
      document.dispatchEvent(new CustomEvent('theme:loading:start'));

      try {
        const data = await fetchShopifySections(target.pathname, sectionId);
        const heroHtml = extractSectionHtml(data, sectionId);
        if (!heroHtml) throw new Error('Missing section HTML');

        let newRoot;

        PdpHero.#preserveScrollPosition(() => {
          newRoot = replaceSectionRoot(this.#root, heroHtml, PdpHero);
        });

        if (newRoot) {
          this.#updatePageMeta(newRoot);
        }

        if (pushState) {
          history.pushState({ pdpHeroUrl: target.pathname }, '', target.pathname);
        }

        PdpHero.#dispatchProductChanged(target.pathname);

        if (scrollToHero) {
          newRoot?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        console.error('PdpHero: in-page product switch failed', error);
        window.location.href = target.pathname;
      } finally {
        document
          .querySelector(`[data-section-type="pdp-hero"][data-section-id="${sectionId}"]`)
          ?.classList.remove('is-loading');
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
      }
    }

    #initSampleSelector(signal) {
      const selector = this.#root.querySelector('[data-pdp-hero-sample-selector]');
      if (!selector) return;

      initPagedCarousel({
        root: selector,
        signal,
        cardsPerPage: PdpHero.CARDS_PER_PAGE,
      });

      const panels = selector.querySelectorAll('[data-pdp-hero-style-panel]');
      const viewBtns = selector.querySelectorAll('[data-pdp-hero-view-btn]');

      const setView = (mode) => {
        const viewMode = PdpHero.persistSampleSelectorView(mode);

        viewBtns.forEach((button) => {
          const isActive = button.getAttribute('data-pdp-hero-view-btn') === viewMode;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        panels.forEach((panel) => {
          panel.classList.toggle(
            'is-active',
            panel.getAttribute('data-pdp-hero-style-panel') === viewMode
          );
        });
      };

      setView(PdpHero.getSampleSelectorView());

      viewBtns.forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            setView(button.getAttribute('data-pdp-hero-view-btn') || 'carousel');
          },
          { signal }
        );
      });
    }

    #bindProductSwitchLinks(signal) {
      this.#root.addEventListener(
        'click',
        (event) => {
          const link = event.target.closest('[data-pdp-hero-product-link]');
          if (!link || !this.#root.contains(link)) return;

          if (link.classList.contains('is-selected') || link.getAttribute('aria-current') === 'page') {
            event.preventDefault();
            return;
          }

          if (link.hasAttribute('data-pdp-hero-coll-tab') && link.classList.contains('is-active')) {
            event.preventDefault();
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();

          const productUrl = link.getAttribute('data-product-url') || link.getAttribute('href');
          const productTitle =
            link.getAttribute('data-product-finish') ||
            link.getAttribute('data-product-title');
          if (!productUrl) return;

          this.#navigateToProduct(productUrl);
        },
        { capture: true, signal }
      );
    }

    #connect() {
      const signal = this.#abortController.signal;
      const form = this.#root.querySelector('form[action*="/cart/add"]');

      this.#root.querySelectorAll('[data-pdp-hero-thumb][data-media-id]').forEach((thumb) => {
        thumb.addEventListener(
          'click',
          () => {
            const mediaId = thumb.getAttribute('data-media-id');
            if (!mediaId) return;

            this.#root.querySelectorAll('[data-pdp-hero-thumb]').forEach((el) => {
              el.classList.toggle('is-active', el === thumb);
            });

            this.#root.querySelectorAll('[data-pdp-hero-main-img]').forEach((img) => {
              const isMatch = img.getAttribute('data-media-id') === mediaId;
              img.classList.toggle('is-visible', isMatch);
              img.classList.toggle('is-hidden', !isMatch);
            });

            const activeImg = this.#root.querySelector(
              `[data-pdp-hero-main-img][data-media-id="${mediaId}"]`
            );
            const selThumb = this.#root.querySelector('[data-pdp-hero-sel-thumb]');

            if (activeImg && selThumb) {
              selThumb.src = activeImg.currentSrc || activeImg.src;
              selThumb.alt = activeImg.alt;
            }
          },
          { signal }
        );
      });

      const qtyInput = this.#root.querySelector('[data-pdp-hero-qty-input]');
      const qtyDecrease = this.#root.querySelector('[data-pdp-hero-qty-decrease]');
      const qtyIncrease = this.#root.querySelector('[data-pdp-hero-qty-increase]');

      const clampQty = (value) => {
        const min = parseInt(qtyInput?.min || '1', 10);
        const max = parseInt(qtyInput?.max || '10', 10);
        return Math.min(Math.max(value, min), max);
      };

      qtyDecrease?.addEventListener(
        'click',
        () => {
          if (!qtyInput) return;
          qtyInput.value = String(clampQty(parseInt(qtyInput.value || '1', 10) - 1));
        },
        { signal }
      );

      qtyIncrease?.addEventListener(
        'click',
        () => {
          if (!qtyInput) return;
          qtyInput.value = String(clampQty(parseInt(qtyInput.value || '1', 10) + 1));
        },
        { signal }
      );

      if (form) {
        form.addEventListener(
          'submit',
          async (event) => {
            const submitter = event.submitter;
            if (!submitter || submitter.getAttribute('data-action') !== 'add-to-cart') return;

            event.preventDefault();
            await this.#addToCart(form, submitter);
          },
          { signal }
        );

        this.#root.querySelector('[data-action="buy-now"]')?.addEventListener(
          'click',
          async (event) => {
            event.preventDefault();
            const button = event.currentTarget;
            const added = await this.#addToCart(form, button);
            if (added) {
              window.location.href = '/checkout';
            }
          },
          { signal }
        );
      }

      this.#bindProductSwitchLinks(signal);
      this.#initSampleSelector(signal);
    }

    destroy() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }
  }

  registerSectionController(PdpHero, PdpHero.ROOT_SELECTOR, {
    beforeInitAll: () => PdpHero.bindPopstate(),
  });

  class FourColumnFeatures {
    #abortController = new AbortController();
    #root;

    static ROOT_SELECTOR = '[data-section-type="four-column-features"]';
    static CARD_SELECTOR = '[data-four-column-features-card]';

    constructor(root) {
      this.#root = root;
      this.#connect();
    }

    #connect() {
      const signal = this.#abortController.signal;

      this.#root.addEventListener(
        'click',
        (event) => {
          const card = event.target.closest(FourColumnFeatures.CARD_SELECTOR);
          if (!card || !this.#root.contains(card)) return;

          event.preventDefault();

          const productUrl = card.getAttribute('data-product-url');

          if (!productUrl) return;

          PdpHero.selectProduct(productUrl, { scrollToTop: true });
        },
        { signal }
      );

      document.addEventListener(
        'pdp-hero:product-changed',
        (event) => {
          if (!document.contains(this.#root)) return;
          this.#setActiveCard(event.detail?.handle);
        },
        { signal }
      );
    }

    #setActiveCard(handle) {
      if (!handle) return;

      this.#root.querySelectorAll(FourColumnFeatures.CARD_SELECTOR).forEach((card) => {
        const isActive = card.getAttribute('data-product-handle') === handle;
        card.classList.toggle('is-selected', isActive);
        card.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    destroy() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }
  }

  registerSectionController(FourColumnFeatures, FourColumnFeatures.ROOT_SELECTOR);

  class PdpReviews {
    #abortController = new AbortController();
    #root;
    #swiper = null;

    static ROOT_SELECTOR = '[data-section-type="pdp-reviews"]';

    constructor(root) {
      this.#root = root;
      this.#connect();
    }

    #connect() {
      this.#initSwiper(0);
    }

    #initSwiper(attempts) {
      const signal = this.#abortController.signal;
      const swiperEl = this.#root.querySelector('[data-pdp-reviews-swiper]');

      if (!swiperEl) return;

      if (typeof Swiper === 'undefined') {
        if (attempts > 200 || signal.aborted) return;
        setTimeout(() => this.#initSwiper(attempts + 1), 25);
        return;
      }

      const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
      if (slideCount === 0) return;

      if (this.#swiper) return;

      const autoplayEnabled = this.#root.getAttribute('data-autoplay') === 'true';
      const autoplayDelay = parseInt(this.#root.getAttribute('data-autoplay-delay') || '5000', 10);

      this.#swiper = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 18,
        loop: slideCount > 3,
        autoplay: autoplayEnabled
          ? {
              delay: autoplayDelay,
              disableOnInteraction: false,
            }
          : false,
        pagination: {
          el: this.#root.querySelector('[data-pdp-reviews-pagination]'),
          clickable: true,
        },
        navigation: {
          nextEl: this.#root.querySelector('[data-pdp-reviews-next]'),
          prevEl: this.#root.querySelector('[data-pdp-reviews-prev]'),
        },
        breakpoints: {
          768: {
            slidesPerView: 2,
          },
          1100: {
            slidesPerView: 3,
          },
        },
      });

      signal.addEventListener('abort', () => {
        this.#swiper?.destroy(true, true);
        this.#swiper = null;
      });
    }

    destroy() {
      this.#swiper?.destroy(true, true);
      this.#swiper = null;
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }
  }

  registerSectionController(PdpReviews, PdpReviews.ROOT_SELECTOR);

  document.addEventListener('shopify:section:load', (event) => {
    initSectionControllers(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroySectionControllers(event.target);
  });

  const ScrollReveal = {
    SELECTOR: '[data-scroll-reveal]',
    REVEALED_CLASS: 'is-scroll-revealed',
    observer: null,

    initAll(scope = document) {
      if (typeof IntersectionObserver === 'undefined') {
        scope.querySelectorAll(ScrollReveal.SELECTOR).forEach((el) => {
          el.classList.add(ScrollReveal.REVEALED_CLASS);
        });
        return;
      }

      if (!ScrollReveal.observer) {
        ScrollReveal.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add(ScrollReveal.REVEALED_CLASS);
              ScrollReveal.observer.unobserve(entry.target);
            });
          },
          { threshold: 0.1, rootMargin: '0px 0px -5% 0px' }
        );
      }

      scope.querySelectorAll(ScrollReveal.SELECTOR).forEach((el) => {
        if (el.classList.contains(ScrollReveal.REVEALED_CLASS)) return;
        ScrollReveal.observer.observe(el);
      });
    },
  };

  ScrollReveal.initAll();

  document.addEventListener('shopify:section:load', (event) => {
    ScrollReveal.initAll(event.target);
  });

  class LpNavbar extends HTMLElement {
    #abortController = new AbortController();
    #intervalId = null;
    #current = 0;

    static MOBILE_QUERY = '(max-width: 900px)';
    static ROTATE_MS = 3000;
    static PILL_ACTIVE_CLASS = 'lp-navbar__pill--active';

    connectedCallback() {
      this.#initPillRotation(this.#abortController.signal);
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      this.#stopRotation();
      this.#current = 0;
    }

    #initPillRotation(signal) {
      const pills = this.querySelectorAll('.lp-navbar__pill');
      if (!pills.length) return;

      const mobileQuery = window.matchMedia(LpNavbar.MOBILE_QUERY);

      const showPill = (index) => {
        pills.forEach((pill) => {
          pill.classList.remove(LpNavbar.PILL_ACTIVE_CLASS);
        });
        pills[index]?.classList.add(LpNavbar.PILL_ACTIVE_CLASS);
      };

      const rotate = () => {
        this.#current = (this.#current + 1) % pills.length;
        showPill(this.#current);
      };

      const startRotation = () => {
        if (this.#intervalId) return;
        this.#current = 0;
        showPill(0);
        this.#intervalId = window.setInterval(rotate, LpNavbar.ROTATE_MS);
      };

      const stopRotation = () => {
        this.#stopRotation();
        pills.forEach((pill) => {
          pill.classList.remove(LpNavbar.PILL_ACTIVE_CLASS);
        });
      };

      const syncViewport = () => {
        if (mobileQuery.matches) {
          startRotation();
        } else {
          stopRotation();
        }
      };

      syncViewport();
      mobileQuery.addEventListener('change', syncViewport, { signal });
    }

    #stopRotation() {
      if (this.#intervalId) {
        window.clearInterval(this.#intervalId);
        this.#intervalId = null;
      }
    }
  }

  class RecentlyViewedCollections extends HTMLElement {
    #abortController = new AbortController();

    static STORAGE_KEY = 'recentlyViewedCollections';
    static STORAGE_CAP = 12;

    connectedCallback() {
      const signal = this.#abortController.signal;
      this.addEventListener('click', this.#onClick, { signal });

      this.#saveCurrentFromPage();
      this.#render();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    #onClick = (event) => {
      const clearBtn = event.target.closest('[data-rvc-clear]');
      if (!clearBtn || !this.contains(clearBtn)) return;
      event.preventDefault();
      this.#clearHistory();
      this.#render();
    };

    #readJson(selector) {
      const el = document.querySelector(selector);
      if (!el) return null;
      try {
        return JSON.parse(el.textContent.trim());
      } catch {
        return null;
      }
    }

    #getHistory() {
      try {
        const raw = localStorage.getItem(RecentlyViewedCollections.STORAGE_KEY);
        const parsed = JSON.parse(raw || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }

    #setHistory(items) {
      try {
        localStorage.setItem(
          RecentlyViewedCollections.STORAGE_KEY,
          JSON.stringify(items.slice(0, RecentlyViewedCollections.STORAGE_CAP))
        );
      } catch {
        // Safari private mode, etc.
      }
    }

    #saveCurrentFromPage() {
      const current = this.#readJson('[data-recently-viewed-collection]');
      if (!current?.handle) return;

      const items = this.#getHistory().filter((item) => item?.handle && item.handle !== current.handle);
      items.unshift({
        ...current,
        viewedAt: Date.now(),
      });
      this.#setHistory(items);
    }

    #clearHistory() {
      try {
        localStorage.removeItem(RecentlyViewedCollections.STORAGE_KEY);
      } catch {
        // ignore
      }
    }

    #getCurrentHandle() {
      const current = this.#readJson('[data-recently-viewed-collection]');
      return current?.handle || '';
    }

    #getSiblings() {
      const siblings = this.#readJson('[data-recently-viewed-siblings]');
      return Array.isArray(siblings) ? siblings.filter((item) => item?.handle && item?.url) : [];
    }

    #getMaxItems() {
      const max = parseInt(this.getAttribute('data-max-items'), 10);
      return Number.isNaN(max) ? 4 : Math.max(1, max);
    }

    #render() {
      const currentHandle = this.#getCurrentHandle();
      const maxItems = this.#getMaxItems();
      const history = this.#getHistory().filter((item) => item?.handle && item.handle !== currentHandle);
      const useHistory = history.length > 0;
      const cards = (useHistory ? history : this.#getSiblings()).slice(0, maxItems);

      const headingEl = this.querySelector('[data-rvc-heading]');
      const subheadingEl = this.querySelector('[data-rvc-subheading]');
      const clearBtn = this.querySelector('[data-rvc-clear]');
      const grid = this.querySelector('[data-rvc-grid]');
      if (!grid) return;

      if (headingEl) {
        headingEl.textContent = useHistory
          ? this.getAttribute('data-heading-history') || 'Recently Viewed'
          : this.getAttribute('data-heading-fallback') || 'You May Also Like';
      }
      if (subheadingEl) {
        subheadingEl.textContent = useHistory
          ? this.getAttribute('data-subheading-history') || ''
          : this.getAttribute('data-subheading-fallback') || '';
      }
      if (clearBtn) {
        clearBtn.hidden = !useHistory;
        clearBtn.classList.toggle('cko:hidden', !useHistory);
      }

      if (!cards.length) {
        grid.innerHTML = '';
        return;
      }

      grid.innerHTML = cards.map((card) => this.#cardHtml(card)).join('');
    }

    #cardHtml(card) {
      const title = this.#escape(card.title || '');
      const lineLabel = this.#escape(card.lineLabel || '');
      const url = this.#escape(card.url || '#');
      const imageUrl = this.#escape(card.imageUrl || '');
      const fromPrice = this.#escape(card.fromPrice || '');

      const image = imageUrl
        ? `<img src="${imageUrl}" alt="${title}" loading="lazy" width="800" height="1000" class="cko:absolute cko:inset-0 cko:h-full cko:w-full cko:object-cover">`
        : `<span class="cko:flex cko:h-full cko:items-center cko:justify-center cko:text-[12px] cko:text-brand-sage">No image</span>`;

      const priceRow = fromPrice
        ? `<p class="cko:m-0 cko:mt-1 cko:font-sans cko:leading-4">
            <span class="cko:opacity-80 cko:text-[12px] cko:text-[#49473f]">From </span>
            <span class="cko:text-[14px] cko:font-semibold cko:text-brand-orange">${fromPrice}</span>
          </p>`
        : '';

      return `
        <a href="${url}" class="cko:flex cko:flex-col cko:no-underline cko:text-inherit cko:group">
          <div class="cko:relative cko:aspect-[333/416] cko:overflow-hidden cko:rounded-[6px] cko:bg-[#f1edeb]">
            ${image}
          </div>
          <div class="cko:mt-3 cko:rounded-[6px] cko:bg-brand-beige cko:p-3">
            ${lineLabel ? `<p class="cko:m-0 cko:font-sans cko:text-[12px] cko:font-normal cko:leading-[13.5px] cko:tracking-[0.9px] cko:uppercase cko:text-brand-brown">${lineLabel}</p>` : ''}
            <h3 class="cko:mt-[5px] cko:mb-[5px] cko:truncate cko:font-serif cko:text-[18px] cko:font-medium cko:leading-[1.1] cko:text-brand-ink cko:group-hover:text-brand-orange">${title}</h3>
            ${priceRow}
          </div>
        </a>
      `;
    }

    #escape(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  class FinishSwatchStrip extends HTMLElement {
    #abortController = new AbortController();
    #raf = 0;
    #drag = null;
    #suppressClick = false;

    connectedCallback() {
      this.#bind();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#endDrag();
    }

    #bind() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;
      const track = this.querySelector('[data-finish-swatch-track]');
      if (!track) return;

      track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      track.addEventListener('pointerdown', this.#onPointerDown, { signal });
      track.addEventListener('pointermove', this.#onPointerMove, { signal });
      track.addEventListener('pointerup', this.#onPointerUp, { signal });
      track.addEventListener('pointercancel', this.#onPointerUp, { signal });
      track.addEventListener('lostpointercapture', this.#onPointerUp, { signal });
      track.addEventListener('click', this.#onClick, { capture: true, signal });
      window.addEventListener('resize', this.#onResize, { passive: true, signal });

      this.#syncOverflow();
    }

    #onResize = () => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#syncOverflow();
      });
    };

    #onScroll = () => {
      this.#syncOverflow();
    };

    #syncOverflow() {
      const track = this.querySelector('[data-finish-swatch-track]');
      if (!track) return;

      const overflow = track.scrollWidth > track.clientWidth + 1;
      this.classList.toggle('is-scrollable', overflow);

      if (!overflow) {
        this.classList.remove('is-at-end');
        return;
      }

      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      this.classList.toggle('is-at-end', atEnd);
    }

    #onPointerDown = (event) => {
      const track = this.querySelector('[data-finish-swatch-track]');
      if (!track || event.button !== 0) return;
      if (track.scrollWidth <= track.clientWidth + 1) return;

      this.#drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: track.scrollLeft,
        moved: false,
      };
      track.setPointerCapture(event.pointerId);
      event.stopPropagation();
    };

    #onPointerMove = (event) => {
      const track = this.querySelector('[data-finish-swatch-track]');
      if (!track || !this.#drag || this.#drag.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - this.#drag.startX;
      if (Math.abs(deltaX) > 4) {
        this.#drag.moved = true;
        this.classList.add('is-dragging');
      }
      track.scrollLeft = this.#drag.startScrollLeft - deltaX;
      event.stopPropagation();
    };

    #onPointerUp = (event) => {
      if (!this.#drag || this.#drag.pointerId !== event.pointerId) return;
      if (this.#drag.moved) this.#suppressClick = true;
      this.#endDrag();
      event.stopPropagation();
    };

    #endDrag() {
      this.classList.remove('is-dragging');
      this.#drag = null;
    }

    #onClick = (event) => {
      if (!this.#suppressClick) return;
      this.#suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };
  }

  class CollectionCarousel extends HTMLElement {
    #abortController = new AbortController();
    #raf = 0;
    #pageCount = 0;
    #drag = null;
    #suppressClick = false;

    connectedCallback() {
      this.#bind();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#endDrag();
    }

    #bind() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      const track = this.querySelector('[data-collection-carousel-track]');
      if (!track) return;

      track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      track.addEventListener('pointerdown', this.#onPointerDown, { signal });
      track.addEventListener('pointermove', this.#onPointerMove, { signal });
      track.addEventListener('pointerup', this.#onPointerUp, { signal });
      track.addEventListener('pointercancel', this.#onPointerUp, { signal });
      track.addEventListener('lostpointercapture', this.#onPointerUp, { signal });
      this.addEventListener('click', this.#onClick, { signal });
      window.addEventListener('resize', this.#onResize, { passive: true, signal });

      this.#renderDots();
      this.#syncDots();
    }

    #onResize = () => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#renderDots();
        this.#syncDots();
      });
    };

    #onPointerDown = (event) => {
      const track = this.querySelector('[data-collection-carousel-track]');
      if (!track || event.button !== 0) return;
      if (event.target.closest('a, button, input, label, select, textarea, [data-finish-swatch-strip]')) return;
      if (track.scrollWidth <= track.clientWidth + 1) return;

      this.#drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: track.scrollLeft,
        moved: false,
      };
      track.setPointerCapture(event.pointerId);
      track.classList.add('is-dragging');
    };

    #onPointerMove = (event) => {
      const track = this.querySelector('[data-collection-carousel-track]');
      if (!track || !this.#drag || this.#drag.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - this.#drag.startX;
      if (Math.abs(deltaX) > 4) this.#drag.moved = true;
      track.scrollLeft = this.#drag.startScrollLeft - deltaX;
    };

    #onPointerUp = (event) => {
      if (!this.#drag || this.#drag.pointerId !== event.pointerId) return;
      this.#endDrag();
    };

    #endDrag() {
      const track = this.querySelector('[data-collection-carousel-track]');
      if (this.#drag?.moved) this.#suppressClick = true;
      track?.classList.remove('is-dragging');
      this.#drag = null;
    }

    #onClick = async (event) => {
      if (this.#suppressClick) {
        this.#suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const button = event.target.closest('[data-action="add-to-cart"]');
      if (button && this.contains(button)) {
        if (!window.routes?.cartAddUrl || window.theme?.cartType === 'page') return;

        const form = button.closest('form[action*="/cart/add"]');
        if (!form || !this.contains(form)) return;

        event.preventDefault();
        event.stopPropagation();

        button.setAttribute('disabled', 'disabled');
        document.dispatchEvent(new CustomEvent('theme:loading:start'));

        try {
          const response = await fetch(`${window.routes.cartAddUrl}.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(serializeProductForm(form)),
          });

          if (response.ok) {
            const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1', 10);

            this.dispatchEvent(
              new CustomEvent('product:added', {
                bubbles: true,
                detail: {
                  button,
                  variant: null,
                  quantity: Number.isNaN(quantity) ? 1 : quantity,
                },
              })
            );
          } else {
            document.dispatchEvent(new CustomEvent('theme:loading:end'));
          }
        } catch {
          document.dispatchEvent(new CustomEvent('theme:loading:end'));
        } finally {
          button.removeAttribute('disabled');
        }
        return;
      }

      const dot = event.target.closest('[data-collection-carousel-dot]');
      if (!dot || !this.contains(dot)) return;

      const pageIndex = parseInt(dot.getAttribute('data-index') || '0', 10);
      const track = this.querySelector('[data-collection-carousel-track]');
      const cards = this.querySelectorAll('[data-collection-carousel-card]');
      const card = cards[pageIndex];
      if (!track || !card) return;

      track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
      this.#setActiveDot(pageIndex);
    };

    #onScroll = () => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#syncDots();
      });
    };

    #getMetrics() {
      const track = this.querySelector('[data-collection-carousel-track]');
      const cards = [...this.querySelectorAll('[data-collection-carousel-card]')];
      if (!track || !cards.length) {
        return { track: null, cards: [], visibleCount: 0, pageCount: 0 };
      }

      const isWideDesktop = window.matchMedia('(min-width: 1521px)').matches;
      let visibleCount = 4;

      if (!isWideDesktop) {
        const firstCard = cards[0];
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        const cardWidth = firstCard.getBoundingClientRect().width;
        const step = cardWidth + gap;
        visibleCount = step > 0 ? Math.max(1, Math.floor((track.clientWidth + gap) / step)) : 1;
      }

      // Wide desktop assumes 4 cards fit; dots only appear for collections beyond that.
      const overflowCount = Math.max(0, cards.length - visibleCount);
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const pageCount = overflowCount > 0 && maxScroll > 24 ? overflowCount + 1 : 0;

      return { track, cards, visibleCount, pageCount };
    }

    #renderDots() {
      const dotsEl = this.querySelector('[data-collection-carousel-dots]');
      if (!dotsEl) return;

      const { pageCount } = this.#getMetrics();
      this.#pageCount = pageCount;

      if (pageCount <= 1) {
        dotsEl.hidden = true;
        dotsEl.innerHTML = '';
        return;
      }

      dotsEl.hidden = false;
      dotsEl.innerHTML = Array.from({ length: pageCount }, (_, index) => {
        const isActive = index === 0;
        return `<button
          type="button"
          class="collection-carousel__dot${isActive ? ' is-active' : ''}"
          data-collection-carousel-dot
          data-index="${index}"
          aria-label="Go to slide ${index + 1}"
          aria-selected="${isActive ? 'true' : 'false'}"
        ></button>`;
      }).join('');
    }

    #syncDots() {
      const { track, cards, pageCount } = this.#getMetrics();
      if (!track || !cards.length || pageCount <= 1) return;

      if (pageCount !== this.#pageCount) {
        this.#renderDots();
      }

      const trackLeft = track.scrollLeft;
      let activeIndex = 0;
      let closest = Infinity;
      const maxIndex = pageCount - 1;

      for (let index = 0; index <= maxIndex; index += 1) {
        const card = cards[index];
        if (!card) continue;
        const distance = Math.abs(card.offsetLeft - trackLeft);
        if (distance < closest) {
          closest = distance;
          activeIndex = index;
        }
      }

      this.#setActiveDot(activeIndex);
    }

    #setActiveDot(activeIndex) {
      const dots = this.querySelectorAll('[data-collection-carousel-dot]');
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  class BeforeAfterSlider extends HTMLElement {
    #abortController = new AbortController();
    #dragging = false;
    #handle = null;

    connectedCallback() {
      this.#bind();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      this.#dragging = false;
    }

    #bind() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      this.#handle = this.querySelector('[data-ba-handle]');
      if (!this.#handle) return;

      const initial = Number(this.dataset.baPos || '50');
      this.#setPosition(Number.isFinite(initial) ? initial : 50);

      this.#handle.addEventListener('pointerdown', this.#onPointerDown, { signal });
      this.#handle.addEventListener('keydown', this.#onKeyDown, { signal });
      this.addEventListener('pointerdown', this.#onTrackPointerDown, { signal });
    }

    #onTrackPointerDown = (event) => {
      if (event.target.closest('[data-ba-handle]')) return;
      if (event.button != null && event.button !== 0) return;
      this.#beginDrag(event);
      this.#updateFromClientX(event.clientX);
    };

    #onPointerDown = (event) => {
      if (event.button != null && event.button !== 0) return;
      this.#beginDrag(event);
    };

    #beginDrag(event) {
      event.preventDefault();
      event.stopPropagation();
      this.#dragging = true;
      this.#handle?.setPointerCapture?.(event.pointerId);
      this.classList.add('is-dragging');

      const signal = this.#abortController.signal;
      const onMove = (moveEvent) => {
        if (!this.#dragging) return;
        moveEvent.preventDefault();
        moveEvent.stopPropagation();
        this.#updateFromClientX(moveEvent.clientX);
      };
      const onUp = (upEvent) => {
        if (!this.#dragging) return;
        this.#dragging = false;
        this.classList.remove('is-dragging');
        try {
          this.#handle?.releasePointerCapture?.(upEvent.pointerId);
        } catch (_) {}
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };

      window.addEventListener('pointermove', onMove, { signal, passive: false });
      window.addEventListener('pointerup', onUp, { signal });
      window.addEventListener('pointercancel', onUp, { signal });
    }

    #onKeyDown = (event) => {
      const step = event.shiftKey ? 10 : 2;
      let next = null;
      if (event.key === 'ArrowLeft' || event.key === 'Home') {
        event.preventDefault();
        next = event.key === 'Home' ? 0 : this.#currentPos() - step;
      } else if (event.key === 'ArrowRight' || event.key === 'End') {
        event.preventDefault();
        next = event.key === 'End' ? 100 : this.#currentPos() + step;
      }
      if (next == null) return;
      this.#setPosition(next);
    };

    #currentPos() {
      return Number(this.dataset.baPos || '50');
    }

    #updateFromClientX(clientX) {
      const rect = this.getBoundingClientRect();
      if (!rect.width) return;
      const ratio = ((clientX - rect.left) / rect.width) * 100;
      this.#setPosition(ratio);
    }

    #setPosition(value) {
      const clamped = Math.max(0, Math.min(100, value));
      const rounded = Math.round(clamped * 10) / 10;
      this.dataset.baPos = String(rounded);
      this.style.setProperty('--ba-pos', `${rounded}%`);
      if (this.#handle) {
        this.#handle.setAttribute('aria-valuenow', String(Math.round(rounded)));
        this.#handle.setAttribute(
          'aria-valuetext',
          `${Math.round(rounded)} percent before`
        );
      }
    }
  }

  class CardDisplayCarousel extends HTMLElement {
    #abortController = new AbortController();
    #raf = 0;
    #pageCount = 0;

    connectedCallback() {
      this.#bind();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      if (this.#raf) cancelAnimationFrame(this.#raf);
    }

    #bind() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      const track = this.querySelector('[data-card-display-carousel-track]');
      if (!track) return;

      track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      this.addEventListener('click', this.#onClick, { signal });
      window.addEventListener('resize', this.#onResize, { passive: true, signal });

      this.#renderDots();
      this.#syncDots();
      this.#syncArrows();
    }

    #onResize = () => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#renderDots();
        this.#syncDots();
        this.#syncArrows();
      });
    };

    #onClick = (event) => {
      const prev = event.target.closest('[data-card-display-carousel-prev]');
      if (prev && this.contains(prev)) {
        event.preventDefault();
        this.#scrollByPage(-1);
        return;
      }

      const next = event.target.closest('[data-card-display-carousel-next]');
      if (next && this.contains(next)) {
        event.preventDefault();
        this.#scrollByPage(1);
        return;
      }

      const dot = event.target.closest('[data-card-display-carousel-dot]');
      if (!dot || !this.contains(dot)) return;

      const pageIndex = parseInt(dot.getAttribute('data-index') || '0', 10);
      this.#scrollToIndex(pageIndex);
    };

    #onScroll = () => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#syncDots();
        this.#syncArrows();
      });
    };

    #getMetrics() {
      const track = this.querySelector('[data-card-display-carousel-track]');
      const cards = [...this.querySelectorAll('[data-card-display-carousel-card]')];
      if (!track || !cards.length) {
        return { track: null, cards: [], visibleCount: 0, pageCount: 0 };
      }

      const isDesktop = window.matchMedia('(min-width: 990px)').matches;
      let visibleCount = 3;

      if (!isDesktop) {
        const firstCard = cards[0];
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        const cardWidth = firstCard.getBoundingClientRect().width;
        const step = cardWidth + gap;
        visibleCount = step > 0 ? Math.max(1, Math.floor((track.clientWidth + gap) / step)) : 1;
      }

      const overflowCount = Math.max(0, cards.length - visibleCount);
      const pageCount = overflowCount > 0 ? overflowCount + 1 : 0;

      return { track, cards, visibleCount, pageCount };
    }

    #getActiveIndex() {
      const { track, cards, pageCount } = this.#getMetrics();
      if (!track || !cards.length) return 0;

      const maxIndex = Math.max(0, (pageCount > 1 ? pageCount : cards.length) - 1);
      const trackLeft = track.scrollLeft;
      let activeIndex = 0;
      let closest = Infinity;

      for (let index = 0; index <= maxIndex; index += 1) {
        const card = cards[index];
        if (!card) continue;
        const distance = Math.abs(card.offsetLeft - trackLeft);
        if (distance < closest) {
          closest = distance;
          activeIndex = index;
        }
      }

      return activeIndex;
    }

    #scrollToIndex(pageIndex) {
      const track = this.querySelector('[data-card-display-carousel-track]');
      const cards = this.querySelectorAll('[data-card-display-carousel-card]');
      const card = cards[pageIndex];
      if (!track || !card) return;

      track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
      this.#setActiveDot(pageIndex);
      this.#syncArrows();
    }

    #scrollByPage(direction) {
      const { cards, pageCount } = this.#getMetrics();
      if (!cards.length) return;

      const maxIndex = Math.max(0, (pageCount > 1 ? pageCount : cards.length) - 1);
      const nextIndex = Math.min(maxIndex, Math.max(0, this.#getActiveIndex() + direction));
      this.#scrollToIndex(nextIndex);
    }

    #renderDots() {
      const dotsEl = this.querySelector('[data-card-display-carousel-dots]');
      if (!dotsEl) return;

      const { pageCount } = this.#getMetrics();
      this.#pageCount = pageCount;

      if (pageCount <= 1) {
        dotsEl.hidden = true;
        dotsEl.innerHTML = '';
        return;
      }

      dotsEl.hidden = false;
      dotsEl.innerHTML = Array.from({ length: pageCount }, (_, index) => {
        const isActive = index === 0;
        return `<button
          type="button"
          class="card-display-carousel__dot${isActive ? ' is-active' : ''}"
          data-card-display-carousel-dot
          data-index="${index}"
          aria-label="Go to slide ${index + 1}"
          aria-selected="${isActive ? 'true' : 'false'}"
        ></button>`;
      }).join('');
    }

    #syncDots() {
      const { track, cards, pageCount } = this.#getMetrics();
      if (!track || !cards.length || pageCount <= 1) return;

      if (pageCount !== this.#pageCount) {
        this.#renderDots();
      }

      this.#setActiveDot(this.#getActiveIndex());
    }

    #setActiveDot(activeIndex) {
      const dots = this.querySelectorAll('[data-card-display-carousel-dot]');
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    #syncArrows() {
      const prev = this.querySelector('[data-card-display-carousel-prev]');
      const next = this.querySelector('[data-card-display-carousel-next]');
      if (!prev && !next) return;

      const { track, cards } = this.#getMetrics();
      if (!track || !cards.length) {
        prev?.setAttribute('disabled', 'disabled');
        next?.setAttribute('disabled', 'disabled');
        return;
      }

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= maxScroll - 2;

      if (prev) {
        if (atStart) prev.setAttribute('disabled', 'disabled');
        else prev.removeAttribute('disabled');
      }

      if (next) {
        if (atEnd || maxScroll <= 0) next.setAttribute('disabled', 'disabled');
        else next.removeAttribute('disabled');
      }
    }
  }

  class TrustpilotReviewsCarousel extends HTMLElement {
    #abortController = new AbortController();
    #raf = 0;
    #pageCount = 0;
    #desktopQuery = window.matchMedia('(min-width: 1300px)');

    connectedCallback() {
      this.#bind();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      if (this.#raf) cancelAnimationFrame(this.#raf);
    }

    #bind() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      const track = this.querySelector('[data-trustpilot-reviews-track]');
      if (!track) return;

      track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      this.addEventListener('click', this.#onClick, { signal });
      window.addEventListener('resize', this.#onResize, { passive: true, signal });
      this.#desktopQuery.addEventListener?.('change', this.#onResize, { signal });

      this.#renderDots();
      this.#syncDots();
    }

    #onResize = () => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#renderDots();
        this.#syncDots();
      });
    };

    #onClick = (event) => {
      const dot = event.target.closest('[data-trustpilot-reviews-dot]');
      if (!dot || !this.contains(dot)) return;

      const pageIndex = parseInt(dot.getAttribute('data-index') || '0', 10);
      this.#scrollToIndex(pageIndex);
    };

    #onScroll = () => {
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => {
        this.#raf = 0;
        this.#syncDots();
      });
    };

    #isDesktop() {
      return this.#desktopQuery.matches;
    }

    #getMetrics() {
      const track = this.querySelector('[data-trustpilot-reviews-track]');
      const cards = [...this.querySelectorAll('[data-trustpilot-reviews-card]')];
      if (!track || !cards.length) {
        return { track: null, cards: [], visibleCount: 0, pageCount: 0 };
      }

      if (this.#isDesktop()) {
        return { track, cards, visibleCount: cards.length, pageCount: 0 };
      }

      const firstCard = cards[0];
      const styles = window.getComputedStyle(track);
      const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
      const cardWidth = firstCard.getBoundingClientRect().width;
      const step = cardWidth + gap;
      const visibleCount = step > 0 ? Math.max(1, Math.floor((track.clientWidth + gap) / step)) : 1;
      const overflowCount = Math.max(0, cards.length - visibleCount);
      const pageCount = overflowCount > 0 ? overflowCount + 1 : 0;

      return { track, cards, visibleCount, pageCount };
    }

    #getActiveIndex() {
      const { track, cards, pageCount } = this.#getMetrics();
      if (!track || !cards.length || pageCount <= 1) return 0;

      const maxIndex = pageCount - 1;
      const trackLeft = track.scrollLeft;
      let activeIndex = 0;
      let closest = Infinity;

      for (let index = 0; index <= maxIndex; index += 1) {
        const card = cards[index];
        if (!card) continue;
        const distance = Math.abs(card.offsetLeft - trackLeft);
        if (distance < closest) {
          closest = distance;
          activeIndex = index;
        }
      }

      return activeIndex;
    }

    #scrollToIndex(pageIndex) {
      if (this.#isDesktop()) return;

      const track = this.querySelector('[data-trustpilot-reviews-track]');
      const cards = this.querySelectorAll('[data-trustpilot-reviews-card]');
      const card = cards[pageIndex];
      if (!track || !card) return;

      track.scrollTo({ left: card.offsetLeft, behavior: 'smooth' });
      this.#setActiveDot(pageIndex);
    }

    #renderDots() {
      const dotsEl = this.querySelector('[data-trustpilot-reviews-dots]');
      if (!dotsEl) return;

      const { pageCount } = this.#getMetrics();
      this.#pageCount = pageCount;

      if (pageCount <= 1) {
        dotsEl.hidden = true;
        dotsEl.innerHTML = '';
        return;
      }

      dotsEl.hidden = false;
      dotsEl.innerHTML = Array.from({ length: pageCount }, (_, index) => {
        const isActive = index === 0;
        return `<button
          type="button"
          class="trustpilot-reviews__dot${isActive ? ' is-active' : ''}"
          data-trustpilot-reviews-dot
          data-index="${index}"
          aria-label="Go to review ${index + 1}"
          aria-selected="${isActive ? 'true' : 'false'}"
        ></button>`;
      }).join('');
    }

    #syncDots() {
      const { track, cards, pageCount } = this.#getMetrics();
      if (!track || !cards.length || pageCount <= 1) return;

      if (pageCount !== this.#pageCount) {
        this.#renderDots();
      }

      this.#setActiveDot(this.#getActiveIndex());
    }

    #setActiveDot(activeIndex) {
      const dots = this.querySelectorAll('[data-trustpilot-reviews-dot]');
      dots.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  class FeaturedBlog extends HTMLElement {
    #abortController = new AbortController();
    #filtersTrack = null;
    #prevButton = null;
    #nextButton = null;
    #filtersWrap = null;
    #filtersSelect = null;

    connectedCallback() {
      this.#cacheElements();
      this.#bind();
      this.#applyFilter('');
      this.#updateFiltersCarousel();
      requestAnimationFrame(() => this.#updateFiltersCarousel());
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    #cacheElements() {
      this.#filtersWrap = this.querySelector('.featured-blog__filters-wrap');
      this.#filtersTrack = this.querySelector('[data-featured-blog-filters]');
      this.#prevButton = this.querySelector('[data-featured-blog-filters-prev]');
      this.#nextButton = this.querySelector('[data-featured-blog-filters-next]');
      this.#filtersSelect = this.querySelector('[data-featured-blog-select]');
    }

    #bind() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.addEventListener('click', this.#onClick, { signal });
      this.#filtersSelect?.addEventListener('change', this.#onSelectChange, { signal });
      this.#filtersTrack?.addEventListener('scroll', this.#onFiltersScroll, { passive: true, signal });
      window.addEventListener('resize', this.#onResize, { passive: true, signal });
    }

    #onClick = (event) => {
      const prev = event.target.closest('[data-featured-blog-filters-prev]');
      if (prev && this.contains(prev)) {
        event.preventDefault();
        this.#scrollFilters(-1);
        return;
      }

      const next = event.target.closest('[data-featured-blog-filters-next]');
      if (next && this.contains(next)) {
        event.preventDefault();
        this.#scrollFilters(1);
        return;
      }

      const filter = event.target.closest('[data-featured-blog-filter]');
      if (!filter || !this.contains(filter)) return;

      event.preventDefault();
      const tag = filter.getAttribute('data-tag') || '';
      this.#setActiveFilter(filter);
      this.#applyFilter(tag);
      this.#scrollFilterIntoView(filter);
    };

    #onSelectChange = (event) => {
      const select = event.target.closest('[data-featured-blog-select]');
      if (!select || !this.contains(select)) return;

      const tag = select.value || '';
      const filter = [...this.querySelectorAll('[data-featured-blog-filter]')].find(
        (button) => (button.getAttribute('data-tag') || '') === tag
      );

      if (filter) {
        this.#setActiveFilter(filter);
      } else if (this.#filtersSelect) {
        this.#filtersSelect.value = tag;
      }

      this.#applyFilter(tag);
    };

    #onFiltersScroll = () => {
      this.#updateArrowState();
    };

    #onResize = () => {
      this.#updateFiltersCarousel();
    };

    #setActiveFilter(activeFilter) {
      this.querySelectorAll('[data-featured-blog-filter]').forEach((button) => {
        const isActive = button === activeFilter;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (this.#filtersSelect) {
        this.#filtersSelect.value = activeFilter.getAttribute('data-tag') || '';
      }
    }

    #applyFilter(tagHandle) {
      const limit = parseInt(this.getAttribute('data-limit') || '3', 10) || 3;
      const cards = [...this.querySelectorAll('[data-featured-blog-card]')];
      let shown = 0;

      const rankedCards =
        tagHandle === ''
          ? [
              ...cards.filter((card) => (card.getAttribute('data-tags') || '').trim()),
              ...cards.filter((card) => !(card.getAttribute('data-tags') || '').trim()),
            ]
          : cards;

      rankedCards.forEach((card) => {
        const tags = (card.getAttribute('data-tags') || '')
          .split('|')
          .map((value) => value.trim())
          .filter(Boolean);
        const matches = tagHandle === '' || tags.includes(tagHandle);
        const shouldShow = matches && shown < limit;

        if (shouldShow) {
          shown += 1;
          card.hidden = false;
          card.classList.remove('cko:hidden');
        } else {
          card.hidden = true;
          card.classList.add('cko:hidden');
        }

        this.#syncCardBadge(card, tagHandle);
      });
    }

    #syncCardBadge(card, tagHandle) {
      const badge = card.querySelector('[data-featured-blog-badge]');
      if (!badge) return;

      let label = '';

      if (tagHandle) {
        const tags = (card.getAttribute('data-tags') || '').split('|');
        const labels = (card.getAttribute('data-tag-labels') || '').split('|');
        const index = tags.findIndex((value) => value.trim() === tagHandle);
        label = index >= 0 ? (labels[index] || '').trim() : '';

        if (!label) {
          const filter = [...this.querySelectorAll('[data-featured-blog-filter]')].find(
            (button) => (button.getAttribute('data-tag') || '') === tagHandle
          );
          label = filter?.textContent?.trim() || '';
        }
      } else {
        label = (card.getAttribute('data-default-badge') || '').trim();
      }

      badge.textContent = label;
      const hasLabel = Boolean(label);
      badge.hidden = !hasLabel;
      badge.classList.toggle('cko:hidden', !hasLabel);
    }

    #updateFiltersCarousel() {
      const track = this.#filtersTrack;
      const wrap = this.#filtersWrap;
      if (!track || !wrap) return;

      if (getComputedStyle(track).display === 'none') {
        wrap.classList.remove('is-scrollable');
        if (this.#prevButton) this.#prevButton.hidden = true;
        if (this.#nextButton) this.#nextButton.hidden = true;
        return;
      }

      const scrollable = track.scrollWidth > track.clientWidth + 1;
      wrap.classList.toggle('is-scrollable', scrollable);

      if (this.#prevButton) this.#prevButton.hidden = !scrollable;
      if (this.#nextButton) this.#nextButton.hidden = !scrollable;

      if (scrollable) {
        this.#centerFiltersIfPossible();
      } else {
        track.scrollLeft = 0;
        track.classList.add('cko:justify-center');
        track.classList.remove('cko:justify-start');
      }

      this.#updateArrowState();
    }

    #centerFiltersIfPossible() {
      const track = this.#filtersTrack;
      if (!track) return;

      track.classList.remove('cko:justify-center');
      track.classList.add('cko:justify-start');
    }

    #updateArrowState() {
      const track = this.#filtersTrack;
      if (!track) return;

      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= maxScroll - 2;

      if (this.#prevButton) this.#prevButton.disabled = atStart;
      if (this.#nextButton) this.#nextButton.disabled = atEnd;
    }

    #scrollFilters(direction) {
      const track = this.#filtersTrack;
      if (!track) return;

      const amount = Math.max(160, Math.floor(track.clientWidth * 0.6));
      track.scrollBy({ left: direction * amount, behavior: 'smooth' });
    }

    #scrollFilterIntoView(filter) {
      const track = this.#filtersTrack;
      if (!track || !filter) return;

      const trackRect = track.getBoundingClientRect();
      const filterRect = filter.getBoundingClientRect();
      const padding = 16;

      if (filterRect.left < trackRect.left + padding) {
        track.scrollBy({
          left: filterRect.left - trackRect.left - padding,
          behavior: 'smooth',
        });
      } else if (filterRect.right > trackRect.right - padding) {
        track.scrollBy({
          left: filterRect.right - trackRect.right + padding,
          behavior: 'smooth',
        });
      }
    }
  }

  class CkoHeader extends HTMLElement {
    #abortController = new AbortController();
    #activeMegaIndex = -1;
    #drawerHost = null;
    #backdropHost = null;
    #searchOverlayHost = null;
    #searchOverlay = null;
    #miniCartDrawer = null;
    #setMobileOpen = null;
    #setSearchOpen = null;
    #hideMega = null;
    #megaPanels = new Map();
    #megaPanelHosts = new Map();
    #megaBackdrop = null;
    #repositionMega = null;

    static DESKTOP_QUERY = '(min-width: 1000px)';

    connectedCallback() {
      const signal = this.#abortController.signal;
      this.#bindMega(signal);
      this.#bindAccordions(signal);
      this.#bindMobile(signal);
      this.#bindMiniCart(signal);
      this.#bindSearch(signal);
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      this.#miniCartDrawer = null;
      this.#setMobileOpen = null;
      this.#setSearchOpen = null;
      this.#hideMega = null;
      this.#repositionMega = null;
      this.#megaBackdrop?.remove();
      this.#megaBackdrop = null;
      this.#restoreMegaPanels();
      document.documentElement.classList.remove('cko-header-menu-open', 'cko-header-search-open');
      this.#restoreDrawerHost();
      this.#restoreSearchOverlay();
    }

    #restoreMegaPanels() {
      this.#megaPanelHosts.forEach((host, panel) => {
        if (!host?.parent?.isConnected) return;
        panel.removeAttribute('data-cko-mega-portaled');
        panel.style.position = '';
        panel.style.top = '';
        panel.style.left = '';
        panel.style.right = '';
        panel.style.width = '';
        panel.style.zIndex = '';
        if (host.next && host.next.parentElement === host.parent) {
          host.parent.insertBefore(panel, host.next);
        } else {
          host.parent.appendChild(panel);
        }
      });
      this.#megaPanelHosts.clear();
      this.#megaPanels.clear();
    }

    #bindMega(signal) {
      const items = [...this.querySelectorAll('[data-cko-mega-item]')];
      if (!items.length) return;

      const main = this.querySelector('[data-cko-header-main]');
      items.forEach((item) => {
        const panel = item.querySelector('[data-cko-mega-panel]');
        if (panel) this.#megaPanels.set(item, panel);
      });

      const ensureMegaBackdrop = () => {
        if (this.#megaBackdrop) return this.#megaBackdrop;
        const backdrop = document.createElement('div');
        backdrop.className = 'cko-mega__backdrop';
        backdrop.hidden = true;
        backdrop.setAttribute('data-cko-mega-backdrop', '');
        document.body.appendChild(backdrop);
        backdrop.addEventListener('click', () => hideAll(), { signal });
        this.#megaBackdrop = backdrop;
        return backdrop;
      };

      const positionMegaBackdrop = () => {
        const backdrop = ensureMegaBackdrop();
        if (!main || this.#activeMegaIndex < 0) {
          backdrop.hidden = true;
          return;
        }
        const rect = main.getBoundingClientRect();
        backdrop.style.top = `${rect.bottom}px`;
        backdrop.hidden = false;
      };

      const portalPanel = (panel) => {
        if (!panel || panel.parentElement === document.body) return;
        this.#megaPanelHosts.set(panel, {
          parent: panel.parentElement,
          next: panel.nextSibling,
        });
        panel.setAttribute('data-cko-mega-portaled', '');
        document.body.appendChild(panel);
      };

      const restorePanel = (panel) => {
        if (!panel || !this.#megaPanelHosts.has(panel)) return;
        const host = this.#megaPanelHosts.get(panel);
        panel.removeAttribute('data-cko-mega-portaled');
        panel.style.position = '';
        panel.style.top = '';
        panel.style.left = '';
        panel.style.right = '';
        panel.style.width = '';
        panel.style.zIndex = '';
        if (host?.parent?.isConnected) {
          if (host.next && host.next.parentElement === host.parent) {
            host.parent.insertBefore(panel, host.next);
          } else {
            host.parent.appendChild(panel);
          }
        }
        this.#megaPanelHosts.delete(panel);
      };

      const positionPanel = (item, panel) => {
        if (!panel) return;
        const kind = item.getAttribute('data-cko-mega-kind');
        if (kind === 'simple') {
          restorePanel(panel);
          panel.style.top = '';
          panel.style.left = '';
          panel.style.right = '';
          panel.style.width = '';
          return;
        }
        if (!main) return;

        portalPanel(panel);

        const mainRect = main.getBoundingClientRect();
        const trigger = item.querySelector('[data-cko-mega-trigger]');
        const triggerRect = trigger?.getBoundingClientRect();
        const panelWidth = 420;
        const gutter = 16;
        let left = triggerRect ? triggerRect.left : Math.max(gutter, mainRect.left + gutter);
        const maxLeft = window.innerWidth - panelWidth - gutter;
        left = Math.max(gutter, Math.min(left, maxLeft));

        panel.style.position = 'fixed';
        panel.style.top = `${mainRect.bottom}px`;
        panel.style.left = `${Math.round(left)}px`;
        panel.style.right = 'auto';
        panel.style.width = 'auto';
        panel.style.zIndex = '9998';
      };

      const clearCloseTimer = (panel) => {
        if (!panel || !panel._ckoMegaCloseTimer) return;
        window.clearTimeout(panel._ckoMegaCloseTimer);
        panel._ckoMegaCloseTimer = null;
      };

      const hidePanel = (item, { animate = true } = {}) => {
        const panel = this.#megaPanels.get(item);
        const trigger = item.querySelector('[data-cko-mega-trigger]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (!panel) return;

        clearCloseTimer(panel);

        const finishHide = () => {
          panel.classList.remove('is-open', 'is-closing');
          panel.hidden = true;
          restorePanel(panel);
        };

        if (!animate || !panel.classList.contains('is-open') || panel.hidden) {
          finishHide();
          return;
        }

        panel.classList.add('is-closing');
        panel.classList.remove('is-open');
        panel._ckoMegaCloseTimer = window.setTimeout(finishHide, 220);
      };

      const hideAll = ({ animate = true } = {}) => {
        items.forEach((item) => hidePanel(item, { animate }));
        this.#activeMegaIndex = -1;
        if (this.#megaBackdrop) this.#megaBackdrop.hidden = true;
      };

      this.#hideMega = () => hideAll({ animate: false });

      const repositionActiveMega = () => {
        if (this.#activeMegaIndex < 0) return;
        const item = items[this.#activeMegaIndex];
        const panel = this.#megaPanels.get(item);
        if (!panel || panel.hidden) return;
        positionPanel(item, panel);
        positionMegaBackdrop();
      };

      this.#repositionMega = repositionActiveMega;

      const showMenu = (index) => {
        hideAll({ animate: false });
        const item = items[index];
        const panel = this.#megaPanels.get(item);
        const trigger = item?.querySelector('[data-cko-mega-trigger]');
        if (!panel || !trigger) return;
        this.#activeMegaIndex = index;
        clearCloseTimer(panel);
        panel.classList.remove('is-open');
        panel.classList.add('is-closing');
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        positionPanel(item, panel);
        positionMegaBackdrop();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.#activeMegaIndex !== index) return;
            panel.classList.remove('is-closing');
            panel.classList.add('is-open');
          });
        });
      };

      items.forEach((item, index) => {
        const trigger = item.querySelector('[data-cko-mega-trigger]');
        const panel = this.#megaPanels.get(item);
        if (!trigger || !panel) return;

        trigger.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (this.#activeMegaIndex === index) {
              hideAll({ animate: true });
            } else {
              showMenu(index);
            }
          },
          { signal }
        );
      });

      const isMegaMenuTarget = (target) => {
        if (!(target instanceof Element)) return false;
        return Boolean(
          target.closest('[data-cko-mega-panel]') ||
            target.closest('[data-cko-mega-trigger]') ||
            target.closest('[data-cko-mega-backdrop]')
        );
      };

      document.addEventListener(
        'click',
        (event) => {
          if (this.#activeMegaIndex < 0) return;
          if (isMegaMenuTarget(event.target)) return;
          hideAll();
        },
        { signal, capture: true }
      );

      let megaScrollFrame = 0;
      const scheduleMegaReposition = () => {
        if (megaScrollFrame) return;
        megaScrollFrame = window.requestAnimationFrame(() => {
          megaScrollFrame = 0;
          repositionActiveMega();
        });
      };

      window.addEventListener('scroll', scheduleMegaReposition, { passive: true, signal });
      window.addEventListener(
        'resize',
        () => {
          scheduleMegaReposition();
          if (!window.matchMedia(CkoHeader.DESKTOP_QUERY).matches) hideAll();
        },
        { signal }
      );

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') hideAll();
        },
        { signal }
      );
    }

    #bindMobile(signal) {
      const openBtn = this.querySelector('[data-cko-menu-open]');
      const closeBtn = this.querySelector('[data-cko-menu-close]');
      const backdrop = this.querySelector('[data-cko-menu-backdrop]');
      const drawer = this.querySelector('[data-cko-menu-drawer]');
      if (!openBtn || !drawer) return;

      // Sticky header creates a containing block for position:fixed.
      // Move drawer/backdrop to body so they cover the full viewport.
      if (backdrop && backdrop.parentElement !== document.body) {
        this.#backdropHost = { parent: backdrop.parentElement, next: backdrop.nextSibling };
        document.body.appendChild(backdrop);
      }
      if (drawer.parentElement !== document.body) {
        this.#drawerHost = { parent: drawer.parentElement, next: drawer.nextSibling };
        document.body.appendChild(drawer);
      }

      const setOpen = (open) => {
        drawer.classList.toggle('is-open', open);
        drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
        openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (backdrop) {
          backdrop.hidden = !open;
          backdrop.classList.toggle('is-open', open);
        }
        document.documentElement.classList.toggle('cko-header-menu-open', open);
      };

      this.#setMobileOpen = setOpen;

      openBtn.addEventListener(
        'click',
        () => {
          this.#setSearchOpen?.(false);
          setOpen(true);
        },
        { signal }
      );
      closeBtn?.addEventListener('click', () => setOpen(false), { signal });
      backdrop?.addEventListener('click', () => setOpen(false), { signal });
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape') setOpen(false);
        },
        { signal }
      );
    }

    #restoreDrawerHost() {
      const restore = (el, host) => {
        if (!el || !host?.parent || !host.parent.isConnected) return;
        if (host.next && host.next.parentElement === host.parent) {
          host.parent.insertBefore(el, host.next);
        } else {
          host.parent.appendChild(el);
        }
      };

      restore(document.querySelector('[data-cko-menu-drawer]'), this.#drawerHost);
      restore(document.querySelector('[data-cko-menu-backdrop]'), this.#backdropHost);
      this.#drawerHost = null;
      this.#backdropHost = null;
    }

    #restoreSearchOverlay() {
      const overlay = this.#searchOverlay;
      const host = this.#searchOverlayHost;
      if (!overlay || !host?.parent || !host.parent.isConnected) {
        this.#searchOverlay = null;
        this.#searchOverlayHost = null;
        return;
      }
      if (host.next && host.next.parentElement === host.parent) {
        host.parent.insertBefore(overlay, host.next);
      } else {
        host.parent.appendChild(overlay);
      }
      this.#searchOverlay = null;
      this.#searchOverlayHost = null;
    }

    #bindSearch(signal) {
      const overlay = this.querySelector('[data-cko-search-overlay]');
      const openButtons = [...this.querySelectorAll('[data-cko-search-open]')];
      if (!overlay || !openButtons.length) return;

      const closeBtn = overlay.querySelector('[data-cko-search-close]');
      const backdrop = overlay.querySelector('[data-cko-search-backdrop]');
      const form = overlay.querySelector('[data-cko-search-form]');
      const input = overlay.querySelector('[data-cko-search-input]');
      const results = overlay.querySelector('[data-cko-search-results]');
      if (!input || !results) return;

      if (overlay.parentElement !== document.body) {
        this.#searchOverlayHost = { parent: overlay.parentElement, next: overlay.nextSibling };
        document.body.appendChild(overlay);
      }
      this.#searchOverlay = overlay;

      let lastQuery = '';
      let lastFocused = null;
      let debounceTimer = null;

      const setOpen = (open, trigger) => {
        overlay.hidden = !open;
        overlay.classList.toggle('is-open', open);
        overlay.setAttribute('aria-hidden', open ? 'false' : 'true');
        openButtons.forEach((btn) => btn.setAttribute('aria-expanded', open ? 'true' : 'false'));
        document.documentElement.classList.toggle('cko-header-search-open', open);

        if (open) {
          lastFocused = trigger || document.activeElement;
          this.#hideMega?.();
          this.#setMobileOpen?.(false);
          this.#miniCartDrawer?.setOpen(false);
          window.requestAnimationFrame(() => input.focus());
        } else {
          overlay.classList.remove('is-loading');
          if (lastFocused && typeof lastFocused.focus === 'function') {
            lastFocused.focus();
          }
        }
      };

      this.#setSearchOpen = setOpen;

      openButtons.forEach((btn) => {
        btn.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            event.stopPropagation();
            setOpen(true, btn);
          },
          { signal }
        );
      });

      closeBtn?.addEventListener('click', () => setOpen(false), { signal });
      backdrop?.addEventListener('click', () => setOpen(false), { signal });
      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && !overlay.hidden) {
            event.preventDefault();
            setOpen(false);
          }
        },
        { signal }
      );

      let skuIndexCache = null;
      let skuIndexPromise = null;

      const looksLikeColonSku = (value) => /:/.test(String(value || ''));

      const loadSkuIndex = () => {
        if (skuIndexCache) return Promise.resolve(skuIndexCache);
        if (skuIndexPromise) return skuIndexPromise;
        const url = window.routes?.skuSearchIndexUrl;
        if (!url) {
          skuIndexCache = {};
          return Promise.resolve(skuIndexCache);
        }
        skuIndexPromise = fetch(url, { method: 'GET', credentials: 'same-origin' })
          .then((response) => (response.ok ? response.json() : {}))
          .then((data) => {
            skuIndexCache = data && typeof data === 'object' ? data : {};
            return skuIndexCache;
          })
          .catch(() => {
            skuIndexCache = {};
            return skuIndexCache;
          });
        return skuIndexPromise;
      };

      const collapseSku = (value) => String(value || '').toLowerCase().replace(/[:\-\s_]/g, '');

      const lookupSkuHits = (index, searchQuery) => {
        const q = String(searchQuery || '').trim().toLowerCase();
        if (!q || !index) return [];
        const collapsedQuery = collapseSku(q);
        const hits = [];
        const seen = new Set();
        const limit = q.endsWith(':') || /:$/.test(q) || q.split(':')[1]?.length <= 4 ? 12 : 8;

        const push = (entry, rank) => {
          if (!entry?.h || !entry?.v || seen.has(entry.v)) return;
          seen.add(entry.v);
          hits.push({ ...entry, rank });
        };

        // Exact SKU
        if (index[q]) push(index[q], 0);

        // Collapsed exact (ESWP1884 == ES:WP1884)
        Object.values(index).forEach((entry) => {
          const sku = String(entry?.s || '').toLowerCase();
          if (!sku) return;
          if (collapseSku(sku) === collapsedQuery) push(entry, 1);
        });

        // Prefix matches (ES:WP → all ES:WP#### pantries)
        const prefixHits = [];
        Object.entries(index).forEach(([key, entry]) => {
          if (!entry?.h || !entry?.v || seen.has(entry.v)) return;
          const sku = String(entry?.s || '').toLowerCase();
          if (key.startsWith(q) || sku.startsWith(q)) {
            prefixHits.push(entry);
            return;
          }
          if (collapsedQuery.length >= 3 && collapseSku(sku).startsWith(collapsedQuery)) {
            prefixHits.push(entry);
          }
        });

        prefixHits
          .sort((a, b) => String(a.s || '').localeCompare(String(b.s || ''), undefined, { numeric: true }))
          .forEach((entry) => push(entry, 2));

        return hits.sort((a, b) => {
          if (a.rank !== b.rank) return a.rank - b.rank;
          return String(a.s || '').localeCompare(String(b.s || ''), undefined, { numeric: true });
        }).slice(0, limit);
      };

      const runSearch = () => {
        const query = input.value.trim();
        lastQuery = query;

        if (!query) {
          results.innerHTML = '';
          results.hidden = true;
          overlay.classList.remove('is-loading');
          return;
        }

        const searchUrl = window.routes?.searchUrl;
        const predictiveUrl = window.routes?.predictiveSearchUrl || `${window.routes?.rootUrlWithoutSlash || ''}/search/suggest`;
        if (!searchUrl && !predictiveUrl && !window.routes?.skuSearchIndexUrl) return;

        results.hidden = false;
        overlay.classList.add('is-loading');

        const unavailable = window.theme?.searchUnavailableProducts || 'hide';
        const encoded = encodeURIComponent(query);
        const fields = [
          'title',
          'product_type',
          'variants.title',
          'vendor',
          'variants.sku',
          'variants.barcode',
          'tag',
        ].join(',');

        const formatMoney = (amount) => {
          const format = window.theme?.moneyFormat || '${{amount}}';
          const value = Number(amount);
          if (!Number.isFinite(value)) return String(amount ?? '');
          const fixed = value.toFixed(2);
          const withComma = fixed.replace('.', ',');
          return format
            .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
            .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(value)))
            .replace(/\{\{\s*amount\s*\}\}/g, fixed);
        };

        const escapeHtml = (value) =>
          String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const renderSkuHits = (hits) => {
          if (!hits?.length) {
            return '<div class="search-bar__empty-state"><p class="heading h4">No results found</p></div>';
          }

          const root = window.routes?.rootUrlWithoutSlash || '';
          const items = hits
            .map((entry) => {
              const url = escapeHtml(`${root}/products/${entry.h}?variant=${entry.v}`);
              const title = escapeHtml(entry.t || '');
              const sku = escapeHtml(entry.s || '');
              const price = formatMoney(entry.p);
              return `<a href="${url}" class="search-bar__result-item">
                <div class="search-bar__image-container">
                  <div class="aspect-ratio aspect-ratio--square"></div>
                </div>
                <div class="search-bar__item-info">
                  <p class="search-bar__item-title">${title}</p>
                  <span class="search-bar__item-sku">SKU: ${sku}</span>
                  <span class="search-bar__item-price">${price}</span>
                </div>
              </a>`;
            })
            .join('');

          return `<p class="search-bar__result-category">Products</p>
            <div class="search-bar__result-products">${items}</div>`;
        };

        const findSkuVariant = (product, searchQuery) => {
          const q = String(searchQuery || '').trim().toLowerCase();
          if (!q || !Array.isArray(product?.variants)) return null;

          const variants = product.variants.filter((variant) => variant?.sku);
          const exact = variants.find((variant) => String(variant.sku).trim().toLowerCase() === q);
          if (exact) return exact;

          return (
            variants.find((variant) => {
              const sku = String(variant.sku).trim().toLowerCase();
              return sku.includes(q) || q.includes(sku);
            }) || null
          );
        };

        const renderPredictiveProducts = (products) => {
          if (!products?.length) {
            return '<div class="search-bar__empty-state"><p class="heading h4">No results found</p></div>';
          }

          const items = products
            .slice(0, 6)
            .map((product) => {
              const matchedVariant = findSkuVariant(product, query);
              const image =
                matchedVariant?.image?.url ||
                matchedVariant?.featured_image?.url ||
                matchedVariant?.image ||
                product.featured_image?.url ||
                product.image ||
                product.featured_image ||
                '';
              const alt = escapeHtml(
                matchedVariant?.image?.alt ||
                  product.featured_image?.alt ||
                  product.title ||
                  ''
              );
              const title = escapeHtml(product.title || '');
              const url = escapeHtml(matchedVariant?.url || product.url || '#');
              const price = formatMoney(
                matchedVariant?.price ?? product.price_min ?? product.price
              );
              const sku = matchedVariant?.sku ? String(matchedVariant.sku).trim() : '';
              const skuHtml = sku
                ? `<span class="search-bar__item-sku">SKU: ${escapeHtml(sku)}</span>`
                : '';
              const imageHtml = image
                ? `<img src="${escapeHtml(image)}" alt="${alt}" width="120" height="120" loading="lazy">`
                : '';

              return `<a href="${url}" class="search-bar__result-item">
                <div class="search-bar__image-container">
                  <div class="aspect-ratio aspect-ratio--square">${imageHtml}</div>
                </div>
                <div class="search-bar__item-info">
                  <p class="search-bar__item-title">${title}</p>
                  ${skuHtml}
                  <span class="search-bar__item-price">${price}</span>
                </div>
              </a>`;
            })
            .join('');

          const viewAll =
            products.length > 3
              ? `<button class="search-bar__view-all" type="submit">View all results</button>`
              : '';

          return `<p class="search-bar__result-category">Products</p>
            <div class="search-bar__result-products">${items}</div>${viewAll}`;
        };

        const renderAjaxHtml = (html) => {
          if (lastQuery !== query) return;
          overlay.classList.remove('is-loading');
          const searchContent = document.createElement('div');
          searchContent.innerHTML = (html || '').trim();
          const viewAll = searchContent.querySelector('.search-bar__view-all');
          if (viewAll) searchContent.appendChild(viewAll);
          results.innerHTML = searchContent.innerHTML;
          results.hidden = !results.innerHTML.trim();
        };

        const runPredictiveFallback = () => {
          if (!predictiveUrl || lastQuery !== query) {
            overlay.classList.remove('is-loading');
            return;
          }

          const predictiveEndpoint = `${predictiveUrl}.json?q=${encoded}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=${encodeURIComponent(unavailable)}&resources[options][fields]=${encodeURIComponent(fields)}`;

          fetch(predictiveEndpoint, { method: 'GET', credentials: 'same-origin' })
            .then((response) => {
              if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
              return response.json();
            })
            .then((payload) => {
              if (lastQuery !== query) return;
              overlay.classList.remove('is-loading');
              const products = payload?.resources?.results?.products || [];
              results.innerHTML = renderPredictiveProducts(products);
              results.hidden = !results.innerHTML.trim();
            })
            .catch(() => {
              if (lastQuery !== query) return;
              overlay.classList.remove('is-loading');
            });
        };

        const runShopifySearch = () => {
          if (searchUrl) {
            fetch(
              `${searchUrl}?view=ajax&q=${encoded}&options[prefix]=last&options[unavailable_products]=${encodeURIComponent(unavailable)}&type=product`,
              { method: 'GET', credentials: 'same-origin' }
            )
              .then((response) => {
                if (!response.ok) throw new Error(`Search ajax failed: ${response.status}`);
                return response.text();
              })
              .then((html) => {
                renderAjaxHtml(html);
              })
              .catch(() => {
                runPredictiveFallback();
              });
          } else {
            runPredictiveFallback();
          }
        };

        // Colon SKUs (ES:B18) are tokenized incorrectly by Shopify search and return
        // unrelated products. Resolve them from the theme SKU index instead.
        if (looksLikeColonSku(query)) {
          loadSkuIndex().then((index) => {
            if (lastQuery !== query) return;
            const hits = lookupSkuHits(index, query);
            overlay.classList.remove('is-loading');
            results.innerHTML = renderSkuHits(hits);
            results.hidden = !results.innerHTML.trim();
          });
          return;
        }

        runShopifySearch();
      };

      form?.addEventListener(
        'submit',
        (event) => {
          const query = input.value.trim();
          if (!query) {
            event.preventDefault();
            return;
          }

          // Colon-SKU: exact match → PDP; prefix (e.g. ES:WP) → keep modal results.
          if (looksLikeColonSku(query)) {
            event.preventDefault();
            loadSkuIndex().then((index) => {
              const hits = lookupSkuHits(index, query);
              const exact = hits.find(
                (hit) => String(hit.s || '').toLowerCase() === query.toLowerCase()
              );
              if (exact) {
                const root = window.routes?.rootUrlWithoutSlash || '';
                window.location.href = `${root}/products/${exact.h}?variant=${exact.v}`;
                return;
              }
              runSearch();
            });
          }
        },
        { signal }
      );

      input.addEventListener(
        'input',
        () => {
          window.clearTimeout(debounceTimer);
          debounceTimer = window.setTimeout(runSearch, 250);
        },
        { signal }
      );

      signal.addEventListener('abort', () => {
        window.clearTimeout(debounceTimer);
      });
    }

    #bindMiniCart(signal) {
      this.#miniCartDrawer = initMiniCartDrawer({
        cartRoot: this,
        signal,
        openOnProductAdded: true,
        onOpenChange: (open) => {
          if (open) {
            this.#setMobileOpen?.(false);
            this.#setSearchOpen?.(false);
          }
        },
      });
    }

    #bindAccordions(signal) {
      this.querySelectorAll('[data-cko-accordion]').forEach((item) => {
        const trigger = item.querySelector('[data-cko-accordion-trigger]');
        if (!trigger) return;

        trigger.addEventListener(
          'click',
          () => {
            const open = !item.classList.contains('is-open');
            item.classList.toggle('is-open', open);
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          },
          { signal }
        );
      });
    }

  }

  class CkoFooter extends HTMLElement {
    #abortController = new AbortController();

    static DESKTOP_QUERY = '(min-width: 1000px)';

    connectedCallback() {
      this.#bindAccordions(this.#abortController.signal);
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    #bindAccordions(signal) {
      const desktopQuery = window.matchMedia(CkoFooter.DESKTOP_QUERY);

      this.querySelectorAll('[data-cko-footer-accordion]').forEach((item) => {
        const trigger = item.querySelector('[data-cko-footer-accordion-trigger]');
        if (!trigger) return;

        trigger.addEventListener(
          'click',
          () => {
            if (desktopQuery.matches) return;
            const open = !item.classList.contains('is-open');
            item.classList.toggle('is-open', open);
            trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
          },
          { signal }
        );
      });

      const syncDesktop = () => {
        if (!desktopQuery.matches) return;
        this.querySelectorAll('[data-cko-footer-accordion]').forEach((item) => {
          item.classList.remove('is-open');
          const trigger = item.querySelector('[data-cko-footer-accordion-trigger]');
          if (trigger) trigger.setAttribute('aria-expanded', 'false');
        });
      };

      desktopQuery.addEventListener('change', syncDesktop, { signal });
    }
  }

  class MainHero extends HTMLElement {
    #abortController = new AbortController();
    #imageSwapToken = 0;
    #preloadedSrcs = new Set();

    connectedCallback() {
      this.#bind();
    }

    disconnectedCallback() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
    }

    #bind() {
      this.#abortController.abort();
      this.#abortController = new AbortController();
      const signal = this.#abortController.signal;

      this.addEventListener('click', this.#onClick, { signal });
      this.addEventListener('pointerenter', this.#onPointerEnter, {
        signal,
        capture: true,
      });
      this.addEventListener('focusin', this.#onPointerEnter, { signal });

      this.querySelectorAll('[data-main-hero-swatch][data-hero-src]').forEach((swatch) => {
        this.#preloadHero(swatch.getAttribute('data-hero-src'), swatch.getAttribute('data-hero-srcset'));
      });

      const active =
        this.querySelector('[data-main-hero-swatch][aria-pressed="true"]') ||
        this.querySelector('[data-main-hero-swatch]');
      if (active) this.#activateSwatch(active, { animate: false });
    }

    #onPointerEnter = (event) => {
      const swatch = event.target.closest?.('[data-main-hero-swatch]');
      if (!swatch || !this.contains(swatch)) return;
      this.#preloadHero(swatch.getAttribute('data-hero-src'), swatch.getAttribute('data-hero-srcset'));
    };

    #onClick = (event) => {
      const swatch = event.target.closest('[data-main-hero-swatch]');
      if (!swatch || !this.contains(swatch)) return;

      event.preventDefault();
      event.stopPropagation();
      this.#activateSwatch(swatch, { animate: true });
    };

    #preloadHero(src, srcset) {
      if (!src || this.#preloadedSrcs.has(src)) return Promise.resolve();

      return new Promise((resolve) => {
        const img = new Image();
        const settle = () => {
          this.#preloadedSrcs.add(src);
          resolve();
        };
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
        if (srcset) img.srcset = srcset;
        img.sizes = '(min-width: 990px) 50vw, 100vw';
        img.src = src;
        if (img.complete) settle();
      });
    }

    #activateSwatch(swatch, { animate = true } = {}) {
      const swatches = this.querySelectorAll('[data-main-hero-swatch]');
      swatches.forEach((el) => {
        const isActive = el === swatch;
        el.classList.toggle('main-hero__swatch--active', isActive);
        el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      const heroSrc = swatch.getAttribute('data-hero-src');
      const heroSrcset = swatch.getAttribute('data-hero-srcset');
      const heroAlt = swatch.getAttribute('data-hero-alt');
      const heroImage =
        this.querySelector('[data-main-hero-image]') ||
        this.querySelector('.main-hero__image');

      if (heroSrc && heroImage) {
        const currentSrc = heroImage.currentSrc || heroImage.getAttribute('src') || '';
        const alreadyShowing =
          currentSrc === heroSrc ||
          (heroImage.getAttribute('src') || '') === heroSrc;

        if (!alreadyShowing) {
          this.#swapHeroImage(heroImage, {
            src: heroSrc,
            srcset: heroSrcset,
            alt: heroAlt,
            animate,
          });
        }
      }

      const collectionUrl = swatch.getAttribute('data-collection-url');
      const collectionLink = this.querySelector('[data-main-hero-collection-link]');
      if (collectionUrl && collectionLink) {
        collectionLink.href = collectionUrl;
      }
    }

    #swapHeroImage(heroImage, { src, srcset, alt, animate }) {
      const token = ++this.#imageSwapToken;
      const reduceMotion =
        !animate ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const applyImage = () => {
        if (token !== this.#imageSwapToken) return;

        if (srcset) {
          heroImage.srcset = srcset;
        } else {
          heroImage.removeAttribute('srcset');
        }
        heroImage.sizes = '(min-width: 990px) 50vw, 100vw';
        heroImage.src = src;
        if (alt) heroImage.alt = alt;
      };

      const fadeIn = () => {
        if (token !== this.#imageSwapToken) return;
        heroImage.classList.remove('is-fading');
      };

      const runSwap = () => {
        if (token !== this.#imageSwapToken) return;

        if (reduceMotion) {
          applyImage();
          fadeIn();
          return;
        }

        const onFadedOut = () => {
          if (token !== this.#imageSwapToken) return;
          applyImage();
          // Preloaded, so this should paint immediately from cache.
          requestAnimationFrame(() => requestAnimationFrame(fadeIn));
        };

        heroImage.classList.add('is-fading');

        const onTransitionEnd = (event) => {
          if (event.target !== heroImage || event.propertyName !== 'opacity') return;
          heroImage.removeEventListener('transitionend', onTransitionEnd);
          onFadedOut();
        };

        heroImage.addEventListener('transitionend', onTransitionEnd);
        window.setTimeout(() => {
          heroImage.removeEventListener('transitionend', onTransitionEnd);
          if (heroImage.classList.contains('is-fading') && token === this.#imageSwapToken) {
            onFadedOut();
          }
        }, 400);
      };

      // Wait for the target image before fading out — avoids a long white gap
      // while a large CDN image downloads.
      this.#preloadHero(src, srcset).then(runSwap);
    }
  }

  document.addEventListener('shopify:section:load', (event) => {
    const hero = event.target.querySelector?.('main-hero') || event.target.closest?.('main-hero');
    if (hero && typeof hero.connectedCallback === 'function') {
      // Re-bind after theme editor section reload
      hero.disconnectedCallback?.();
      hero.connectedCallback();
    }

    const carousel =
      event.target.querySelector?.('collection-carousel') ||
      event.target.closest?.('collection-carousel');
    if (carousel && typeof carousel.connectedCallback === 'function') {
      carousel.disconnectedCallback?.();
      carousel.connectedCallback();
    }

    const cardCarousel =
      event.target.querySelector?.('card-display-carousel') ||
      event.target.closest?.('card-display-carousel');
    if (cardCarousel && typeof cardCarousel.connectedCallback === 'function') {
      cardCarousel.disconnectedCallback?.();
      cardCarousel.connectedCallback();
    }

    event.target.querySelectorAll?.('before-after-slider').forEach((slider) => {
      if (typeof slider.connectedCallback === 'function') {
        slider.disconnectedCallback?.();
        slider.connectedCallback();
      }
    });

    const trustpilotCarousel =
      event.target.querySelector?.('trustpilot-reviews-carousel') ||
      event.target.closest?.('trustpilot-reviews-carousel');
    if (trustpilotCarousel && typeof trustpilotCarousel.connectedCallback === 'function') {
      trustpilotCarousel.disconnectedCallback?.();
      trustpilotCarousel.connectedCallback();
    }

    const featuredBlog =
      event.target.querySelector?.('featured-blog') || event.target.closest?.('featured-blog');
    if (featuredBlog && typeof featuredBlog.connectedCallback === 'function') {
      featuredBlog.disconnectedCallback?.();
      featuredBlog.connectedCallback();
    }

    const ckoHeader =
      event.target.querySelector?.('cko-header') || event.target.closest?.('cko-header');
    if (ckoHeader && typeof ckoHeader.connectedCallback === 'function') {
      ckoHeader.disconnectedCallback?.();
      ckoHeader.connectedCallback();
    }

    const ckoFooter =
      event.target.querySelector?.('cko-footer') || event.target.closest?.('cko-footer');
    if (ckoFooter && typeof ckoFooter.connectedCallback === 'function') {
      ckoFooter.disconnectedCallback?.();
      ckoFooter.connectedCallback();
    }
  });

  const components = [
    ['aeo-navbar', AeoNavbar],
    ['aeo-content-hub', AeoContentHub],
    ['aeo-free-samples', AeoFreeSamples],
    ['lp-hero-carousel', LpHeroCarousel],
    ['lp-navbar', LpNavbar],
    ['accordion-list', ListAccordion],
    ['collection-hero', CollectionHero],
    ['collection-products', CollectionProducts],
    ['recently-viewed-collections', RecentlyViewedCollections],
    ['cko-header', CkoHeader],
    ['cko-footer', CkoFooter],
    ['main-hero', MainHero],
    ['collection-carousel', CollectionCarousel],
    ['finish-swatch-strip', FinishSwatchStrip],
    ['card-display-carousel', CardDisplayCarousel],
    ['before-after-slider', BeforeAfterSlider],
    ['trustpilot-reviews-carousel', TrustpilotReviewsCarousel],
    ['featured-blog', FeaturedBlog],
  ];

  components.forEach(([name, constructor]) => {
    if (!customElements.get(name)) {
      customElements.define(name, constructor);
    }
  });
})();
